/* LEGEND — Terminal: composition + state. Ported from terminal.jsx.
   Mock reads → source-backed state; action handlers also fire WS commands in live mode. */
import React from 'react';
import { WidgetCard, Watchlist, DailyMovers, AddWidgetMenu, Icon } from './Widgets';
import { ScannerList } from './ScannerList';
import { NewsRail } from './NewsRail';
import { HeatStrip } from './HeatStrip';
import { SimBar } from './SimBar';
import { StructureMap } from './StructureMap';
import { ChartHeader, CursorLegend } from './ChartHeader';
import { ChartPane } from './ChartPane';
import { SRLevels } from './SRLevels';
import { OrderOverlay } from './OrderOverlay';
import { FloatingCard, PositionBar } from './FloatingCard';
import { SupplyCard } from './SupplyCard';
import { SupplyTimeline } from './SupplyTimeline';
import { StockInfoCard } from './StockInfoCard';
import { PblCard } from './PblCard';
import { BorrowCard } from './BorrowCard';
import { RrCard } from './RrCard';
import { VisionLayer } from './VisionLayer';
import { TrendLineLayer } from './TrendLineLayer';
import { EntryMarksLayer, EM_W } from './EntryMarksLayer';
import { VerdictRibbon } from './VerdictRibbon';
import { JournalCard } from './JournalCard';
import { LadderCard, ladderSignalKind } from './LadderCard';
import { fmtPct, fmtPx4, fmtQty, etClock } from '../lib/format';
import { structureView, LEVEL_PREFS_DEFAULT, railReads } from '../lib/structure';
import { defaultIndicators, TARGET_PRESETS, TRAIL_SOURCES, bucketShares, sizingFor, DEFAULT_SIZE_PARAMS } from '../lib/constants';
import type { SizeParams } from '../lib/constants';
import { MOCK } from '../lib/mode';
import { SOLO, openSoloWindow } from '../lib/solo';
import { useVisionLane } from '../lib/use-vision-lane';
import { useMinuteBars } from '../lib/use-minute-bars';
import { entryMarksFor, parseRecord, ENTRY_RECORD, type EntryRecord } from '../lib/entry-marks';
import { ws } from '../lib/ws';
import { PP } from '../lib/perf';
import { signOut as fbSignOut } from '../lib/firebase';
import {
  getSymbols, getKeyLevels, getSRLevels, getPositions, getAccount, subscribeAccount, getSymbolConfig, getVision, subscribeVision,
  subscribeSymbolStreams, subscribeGlobalTrades, subscribeGlobalOrders, setWatchlist, searchSymbols, subscribeTrades, subscribeLadder, subscribeSymbols,
  getScreenerSeed, subscribeScreener, getScreenerShortSeed, subscribeScreenerShort, getStructure, subscribeStructure,
  getSupply, getPbl, subscribePbl, getRr, getJournal, subscribeJournal, getBorrow, subscribeBorrow, getLocates,
  getNews, subscribeNews, getAbout, getEntryRecord,
  subscribeIndicators, getIndicators, subscribeCommandRejections,
} from '../data/source';
import { genSignal as mockGenSignal, genShortSignal as mockGenShortSignal } from '../data/mock';
import { playPing, playPlaced, playFill, playProfit, playStop, playCancel, playLadderAlarm, unlockAudio } from '../lib/sound';
import { mapTradeToPosition } from '../lib/mappers';
import { createPortal } from 'react-dom';

// The trade journal pages (components/journal, PLAN §9): a full-screen overlay loaded on first open, so the terminal's
// own bundle does not carry it.
const JournalApp = React.lazy(() => import('./journal/JournalApp'));
// The LLM trader's console (components/agent): its day as runs — the scouts, the supervisor, the second opinion, the
// fills — read from the agent's own record. Same overlay pattern as the journal, loaded on first open.
const AgentApp = React.lazy(() => import('./agent/AgentApp'));

const { useState: useStateT, useRef: useRefT, useEffect: useEffectT, useMemo } = React;

const TONES: any = {
  soft: { up: '#38c08a', down: '#ec6a6a' },
  classic: { up: '#15c97e', down: '#ff5862' },
};

function buildVars(t: any) {
  const tone = TONES[t.tone] || TONES.soft;
  const a = t.accent;
  return {
    '--accent': a, '--accent-dim': a + '66', '--accent-bg': a + '22', '--accent-bg-2': a + '12',
    '--up': tone.up, '--up-line': tone.up, '--up-bg': tone.up + '24', '--up-bg-2': tone.up + '12',
    '--down': tone.down, '--down-line': tone.down, '--down-bg': tone.down + '24', '--down-bg-2': tone.down + '12',
  } as any;
}
function chartColors(t: any) {
  const tone = TONES[t.tone] || TONES.soft;
  return {
    bg: '#0a0b0d', text: '#6b7280', line: '#ffffff14', line2: '#ffffff24', grid: '#ffffff07',
    surface: '#16191e', surface3: '#1c2026', cross: '#7b828c',
    up: tone.up, down: tone.down,
    upVol: 'rgba(56,192,138,0.30)', downVol: 'rgba(236,106,106,0.28)',
    accent: t.accent, warn: '#e0b15a',
  };
}

function accountSummary(positions: any, activeSymbol: string, lastPrice: number, meta: any, account: any) {
  let mktVal = 0, openCount = 0;
  const rows: any[] = [];
  Object.keys(positions).forEach((k) => {
    const p = positions[k]; if (!p || p.qty <= 0) return;
    const last = k === activeSymbol ? lastPrice : (p.last ?? meta[k]?.last ?? meta[k]?.base ?? p.avg);
    const dir = p.side === 'long' ? 1 : -1;
    const val = last * p.qty;
    const pl = p.unrealizedPnL != null ? p.unrealizedPnL : (last - p.avg) * dir * p.qty;
    mktVal += val; openCount++;
    rows.push({ sym: k, side: p.side, qty: p.qty, val, pl });
  });
  rows.sort((a, b) => b.val - a.val);
  // Use the REAL account values from /api/account; only fall back to derived figures.
  const cash = account.cash || 0;
  const equity = account.equity || (cash + mktVal);
  const buyingPower = account.buyingPower || cash * 2;
  const dayPL = account.lastEquity ? equity - account.lastEquity : 0;
  const dayPct = account.lastEquity ? dayPL / account.lastEquity : 0;
  return { equity, cash, mktVal, buyingPower, dayPL, dayPct, openCount, rows };
}

function apStat(k: string, v: string, sub?: string, cls?: string) {
  return React.createElement('div', { className: 'ap-cell', key: k },
    React.createElement('div', { className: 'apc-k' }, k),
    React.createElement('div', { className: 'apc-v num ' + (cls || '') }, v),
    sub ? React.createElement('div', { className: 'apc-sub' }, sub) : null);
}

function AccountChip({ summary, user, onSignOut, onOpenJournal, onOpenAgent }: any) {
  const [open, setOpen] = useStateT(false);
  const prof = {
    name: user?.displayName || 'Demo Trader',
    email: user?.email || 'demo@momentum.sim',
    picture: user?.photoURL || null,
  };
  const initials = (prof.name || 'DT').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
  useEffectT(() => {
    if (!open) return;
    const h = (e: any) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);
  const s = summary;
  const up = s.dayPL >= 0;
  const av = (cls?: string) => prof.picture
    ? React.createElement('img', { className: 'acc-av ' + (cls || ''), src: prof.picture, alt: '' })
    : React.createElement('span', { className: 'acc-av ' + (cls || '') }, initials);

  return React.createElement('div', { className: 'account' },
    React.createElement('button', { className: 'account-btn' + (open ? ' on' : ''), onClick: () => setOpen((v) => !v), title: prof.email },
      av(),
      React.createElement('span', { className: 'acc-name' }, prof.name),
      React.createElement('svg', { className: 'chev', width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, style: { transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' } }, React.createElement('path', { d: 'M6 9l6 6 6-6' }))),

    React.createElement('div', { className: 'acc-scrim' + (open ? ' show' : ''), onClick: () => setOpen(false) }),

    React.createElement('aside', { className: 'acc-panel' + (open ? ' open' : ''), 'aria-hidden': !open },
      React.createElement('div', { className: 'ap-head' },
        av('lg'),
        React.createElement('div', { className: 'ap-id' },
          React.createElement('div', { className: 'ap-name' }, prof.name),
          React.createElement('div', { className: 'ap-email' }, prof.email)),
        React.createElement('button', { className: 'icon-btn', onClick: () => setOpen(false), title: 'Close' },
          React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2 }, React.createElement('path', { d: 'M6 6l12 12M18 6L6 18' })))),

      React.createElement('div', { className: 'ap-body' },
        React.createElement('div', { className: 'ap-equity' },
          React.createElement('div', { className: 'ape-k' }, 'Account value'),
          React.createElement('div', { className: 'ape-v num' }, '$' + s.equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })),
          React.createElement('div', { className: 'ape-chg num ' + (up ? 'up' : 'down') },
            React.createElement('svg', { width: 10, height: 10, viewBox: '0 0 8 8', style: { transform: up ? 'none' : 'rotate(180deg)' } }, React.createElement('path', { d: 'M4 1l3 5H1z', fill: 'currentColor' })),
            (up ? '+$' : '−$') + Math.abs(s.dayPL).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            React.createElement('span', { className: 'ape-pct' }, '(' + fmtPct(s.dayPct) + ')'),
            React.createElement('span', { className: 'ape-today' }, 'today'))),

        React.createElement('div', { className: 'ap-grid' },
          apStat('Buying power', '$' + s.buyingPower.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), '2× margin'),
          apStat('Cash available', '$' + s.cash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'settled'),
          apStat('Market value', '$' + s.mktVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), s.openCount + ' open'),
          apStat("Day's P/L", (up ? '+$' : '−$') + Math.abs(s.dayPL).toLocaleString('en-US', { maximumFractionDigits: 0 }), fmtPct(s.dayPct), up ? 'up' : 'down')),
        onOpenJournal ? React.createElement('button', { className: 'ap-journal', title: 'Open the trade journal', onClick: () => { setOpen(false); onOpenJournal(); } },
          React.createElement('span', { className: 'apj-ic' }, React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('path', { d: 'M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4zM5 17a3 3 0 0 1 3-3h11M9 8h6' }))),
          React.createElement('span', { className: 'apj-txt' }, React.createElement('b', null, 'Trade journal'), React.createElement('i', null, 'Every trade reviewed · your grade and notes')),
          React.createElement('span', { className: 'apj-go' }, React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('path', { d: 'M14 4h6v6M20 4l-9 9M18 13v6H5V6h6' })))) : null,
        onOpenAgent ? React.createElement('button', { className: 'ap-journal', title: 'Open the trading agent: its day, close by close', onClick: () => { setOpen(false); onOpenAgent(); } },
          React.createElement('span', { className: 'apj-ic' }, React.createElement('svg', { viewBox: '0 0 24 24', fill: 'currentColor' }, React.createElement('path', { d: 'M12 2.4c.9 5 2.7 7 8 9.6-5.3 2.6-7.1 4.6-8 9.6-.9-5-2.7-7-8-9.6 5.3-2.6 7.1-4.6 8-9.6z' }))),
          React.createElement('span', { className: 'apj-txt' }, React.createElement('b', null, 'Trading agent'), React.createElement('i', null, 'Scouts, supervisor, second opinion · close by close')),
          React.createElement('span', { className: 'apj-go' }, React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, React.createElement('path', { d: 'M14 4h6v6M20 4l-9 9M18 13v6H5V6h6' })))) : null),
        /* Open-positions list removed for now (pending Mongo↔Alpaca reconciliation). */

      React.createElement('div', { className: 'ap-foot' },
        React.createElement('button', { className: 'ap-link' }, 'Account settings'),
        React.createElement('button', { className: 'ap-signout', onClick: onSignOut }, 'Sign out'))));
}

export function Terminal({ t, user }: any) {
  const [symbols, setSymbols] = useStateT<any[]>([]);
  // The trade journal overlay: the account panel opens it; `?journal` (or `?journal=YYYY-MM-DD`, plus `&trade=ID` or
  // `&view=month`) opens it on load where it was — the journal keeps those in the URL while it is open.
  type JournalPlace = { open: boolean; date: string | null; trade: string | null; view: string | null };
  const [journal, setJournal] = useStateT<JournalPlace>(() => {
    const p = new URLSearchParams(window.location.search), q = p.get('journal');
    return q === null ? { open: false, date: null, trade: null, view: null }
      : { open: true, date: /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : null, trade: p.get('trade'), view: p.get('view') === 'month' ? 'month' : null };
  });
  // The trading agent's console: the account panel opens it; `?agent` (or `?agent=YYYY-MM-DD`) opens it on load, and it
  // keeps its day in the URL while it is open.
  const [agent, setAgent] = useStateT<{ open: boolean; date: string | null }>(() => {
    const q = new URLSearchParams(window.location.search).get('agent');
    return q === null ? { open: false, date: null } : { open: true, date: /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : null };
  });
  // Symbol list resolved (even empty or failed). Gates the boot splash: an empty
  // terminal must still render the workspace, because the watchlist search is the
  // only way to add the first symbol.
  const [symbolsReady, setSymbolsReady] = useStateT(false);
  // Solo tab (?symbol=SYM): the pinned symbol is the active symbol from the
  // very first render — the chart mounts before getSymbols() resolves, and the
  // boot/reconcile setters below all keep an existing value (`cur ||`).
  const [activeSymbol, setActive] = useStateT<string>(SOLO ? SOLO.symbol : '');
  const [tf, setTf] = useStateT(SOLO ? SOLO.tf : '1');
  const [indByTf, setIndByTf] = useStateT<any>(defaultIndicators);
  const [positions, setPositions] = useStateT<any>({});
  const [watch, setWatch] = useStateT<string[]>([]);
  const [rowPrices, setRowPrices] = useStateT<Record<string, { last: number; initial: number }>>({});
  // Per-symbol exit config (loaded from /api/symbols/:symbol). Preset defaults NONE, trailing stop OFF.
  const [cfgPreset, setCfgPreset] = useStateT<string>('none');
  const [cfgTrailing, setCfgTrailing] = useStateT<boolean>(false);
  // What the trail follows, per symbol ('levels' | ema9/ema21/ema50/ema200/vwap).
  // A map (not single state) so each symbol keeps its own choice across switches.
  const [trailSrcBySym, setTrailSrcBySym] = useStateT<Record<string, string>>({});
  // Armed trade side, per symbol. Absent = long, always — the same asymmetry the
  // worker enforces (worker/trading-side.ts): a short is only ever an explicit act.
  const [sideBySym, setSideBySym] = useStateT<Record<string, string>>({});
  // Live 1m indicator snapshot for the ACTIVE symbol (worker-published per bar
  // close — the values the trail would actually anchor to). Shown on the
  // position card's trail-source buttons; null until the first frame/seed.
  const [indSnap, setIndSnap] = useStateT<any>(null);
  // Per-symbol sizing override (from /api/symbols/:symbol; fields are usually null).
  const [symbolSizing, setSymbolSizing] = useStateT<any>({});
  const [widgets, setWidgets] = useStateT([{ id: 'w1', type: 'watchlist' }, { id: 'w2', type: 'scanner' }]);
  // ---- screener / automated-signals feed ----
  const [signals, setSignals] = useStateT<any[]>([]);
  // ---- screener SHORT lane (sell-led flushes; own feed, own pause) ----
  const [shortSignals, setShortSignals] = useStateT<any[]>([]);
  const [sigPaused, setSigPaused] = useStateT(false);
  const [sigNewIds, setSigNewIds] = useStateT<string[]>([]);
  const [shortPaused, setShortPaused] = useStateT(false);
  const [shortNewIds, setShortNewIds] = useStateT<string[]>([]);
  const [soundOn, setSoundOn] = useStateT<boolean>(() => {
    try { return localStorage.getItem('legend_sound') !== '0'; } catch { return true; }
  });
  const sigPausedRef = useRefT(false); sigPausedRef.current = sigPaused;
  const shortPausedRef = useRefT(false); shortPausedRef.current = shortPaused;
  const soundOnRef = useRefT(soundOn); soundOnRef.current = soundOn;
  function toggleSound() {
    unlockAudio();
    setSoundOn((v) => { const nv = !v; try { localStorage.setItem('legend_sound', nv ? '1' : '0'); } catch { /* ignore */ } if (nv) playPing(0.6); return nv; });
  }
  // Unlock audio on the first user gesture so surge pings can auto-play later.
  useEffectT(() => {
    const unlock = () => { unlockAudio(); window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
    window.addEventListener('pointerdown', unlock); window.addEventListener('keydown', unlock);
    return () => { window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
  }, []);
  const [collapsed, setCollapsed] = useStateT<any>({});
  const [lastPrice, setLastPrice] = useStateT(0);
  // Latest NBBO for the active symbol ({bid,ask,ts}), or null until the first quote.
  // Rendered in the chart header; see the coalesced subscription further down.
  const [hover, setHover] = useStateT<any>(null);
  const [popover, setPopover] = useStateT<any>(null);
  const [chartType, setChartType] = useStateT('candles');
  const [indOpen, setIndOpen] = useStateT(false);
  const [addOpen, setAddOpen] = useStateT(false);
  const [cardPos, setCardPos] = useStateT<any>({ x: 14, y: 84 });
  const [cardCollapsed, setCardCollapsed] = useStateT(false);
  const [cardOpen, setCardOpen] = useStateT(true);
  // Supply card (supply_timeline model). Right-anchored by default (x:null). Its share trail
  // opens the Float & Dilution timeline panel over the chart.
  const [supplyModel, setSupplyModel] = useStateT<any>(null);
  const [dilCardPos, setDilCardPos] = useStateT<any>({ x: null, y: 84 });
  const [dilCardCollapsed, setDilCardCollapsed] = useStateT(false);
  const [dilOpen, setDilOpen] = useStateT(false); // start closed; open via the "+ Supply" button when needed
  const [supplyTlOpen, setSupplyTlOpen] = useStateT(false);
  // Stock info / news card (about snapshot + stored headlines). Right-anchored;
  // open via the "News" header pill. News frames are a trickle (headline
  // cadence), so parent-fed like Supply; the card owns only its NBBO feed.
  const [newsData, setNewsData] = useStateT<any>(null);
  const [aboutInfo, setAboutInfo] = useStateT<any>(null);
  const [newsCardPos, setNewsCardPos] = useStateT<any>({ x: null, y: 84 });
  const [newsOpen, setNewsOpen] = useStateT(false);
  // Pullback (PBL-v12) signal card. Right-anchored; open via the "+ Pullback" button.
  const [pblReport, setPblReport] = useStateT<any>(null);
  const [pblCardPos, setPblCardPos] = useStateT<any>({ x: null, y: 84 });
  const [pblCardCollapsed, setPblCardCollapsed] = useStateT(false);
  const [pblOpen, setPblOpen] = useStateT(false);
  const [borrowData, setBorrowData] = useStateT<any>(null);
  // Borrow the fleet HOLDS, account-wide, keyed by symbol. Kept at the Terminal
  // level rather than inside the BorrowCard because the point of it is to be
  // visible without opening anything — the header chip reads the same map.
  const [heldLocates, setHeldLocates] = useStateT<Record<string, any>>({});
  const [borrowCardPos, setBorrowCardPos] = useStateT<any>({ x: null, y: 84 });
  const [borrowCardCollapsed, setBorrowCardCollapsed] = useStateT(false);
  const [borrowOpen, setBorrowOpen] = useStateT(false);
  // Live risk/reward card (worker rr:{SYM} preview feed). Right-anchored;
  // open via the "R/R" header pill. The CARD owns its own hydrate+subscribe
  // (HeatStrip rule: frequent feeds re-render only the widget, never this
  // tree) — Terminal keeps just a one-shot availability probe for the pill.
  const [rrAvailable, setRrAvailable] = useStateT(false);
  const [rrCardPos, setRrCardPos] = useStateT<any>({ x: null, y: 84 });
  const [rrOpen, setRrOpen] = useStateT(false);
  // Momentum Vision detection layer (vision-service rules-v0 frames drawn on
  // the chart). ON by default; toggled off/on by the "Vision" header pill. The
  // LAYER owns its hydrate+subscribe (RrCard rule) — Terminal holds only the
  // toggle, so mounting with it on costs nothing until the first frame lands.
  const [visionOpen, setVisionOpen] = useStateT(true);
  // Pullback Trend Lines: a drawing-only chart overlay (resistance through the
  // swing highs, support through the swing lows). Off by default; toggled by
  // the "Trend Lines" header pill. Reads the pane's own candles — no feed.
  const [trendOpen, setTrendOpen] = useStateT(false);
  // Trade journal card (per-symbol round-trip history). Right-anchored; open via
  // the "+ Journal" button.
  const [journalBook, setJournalBook] = useStateT<any>(null);
  const [tjCardPos, setTjCardPos] = useStateT<any>({ x: null, y: 84 });
  const [tjCardCollapsed, setTjCardCollapsed] = useStateT(false);
  const [tjOpen, setTjOpen] = useStateT(false);
  // Ladder card (order-flow when-machine). Self-fetches via its hook, so Terminal
  // only owns open-state + position (unlike Supply, which is parent-fed). Left-anchored.
  const [ladCardPos, setLadCardPos] = useStateT<any>({ x: 14, y: 84 });
  const [ladCardCollapsed, setLadCardCollapsed] = useStateT(false);
  const [ladOpen, setLadOpen] = useStateT(false); // floating mode: open via the "+ Ladder" button
  // Embedded (docked) mode: Ladder ribbon + Position bar are welded to the chart;
  // these toggle their full-widget pop-outs on demand.
  const docked = t.dock !== 'floating';
  const [ladPop, setLadPop] = useStateT(false);
  const [posPop, setPosPop] = useStateT(false);
  // Watchlist ladder alarms: symbol → tone ('flip' bullish / 'feeding' bearish),
  // set when a loud ladder signal fires for a watched symbol, auto-cleared after
  // the illumination window. Refs let the (watch-scoped) subscription read the
  // live active-symbol/card state without re-subscribing.
  const [ladderAlarms, setLadderAlarms] = useStateT<Record<string, 'flip' | 'feeding'>>({});
  const ladAlarmTimersRef = useRefT<Record<string, any>>({});
  const activeSymbolRef = useRefT(activeSymbol); activeSymbolRef.current = activeSymbol;
  // Mirrors for the watchlist live-sync reconciler (reads without re-subscribing).
  const positionsRef = useRefT(positions); positionsRef.current = positions;
  const watchRef = useRefT<string[]>(watch); watchRef.current = watch;
  // ticker → last manual watchlist edit; shields a just-added row from reconcile
  // eviction and a just-removed row from reconcile re-add while the server-side
  // isActivelyTrading write is still landing.
  const manualOpRef = useRefT<Record<string, { ts: number; on: boolean }>>({});
  // The active symbol already has a blipping ladder surface when its card is open —
  // suppress the double cue then, and ONLY then. This used to be `docked || ladOpen`,
  // which muted every embedded-mode symbol because the chart-top ribbon was always
  // mounted; with the ribbon gone that would have silenced the active symbol outright.
  const ladVisibleRef = useRefT(false); ladVisibleRef.current = docked ? ladPop : ladOpen;
  const [toast, setToast] = useStateT<any>(null);
  const [levels, setLevels] = useStateT<any>(null);
  const [srLevels, setSrLevels] = useStateT<any[]>([]);
  const [structure, setStructure] = useStateT<any>(null); // raw level map for the StructureRail
  const [account, setAccount] = useStateT<any>({ equity: 0, cash: 0, buyingPower: 0, portfolioValue: 0, lastEquity: 0 });
  const cash = account.cash;

  // Mobile: the left rail collapses into an off-canvas drawer (desktop shows it inline).
  const [railOpen, setRailOpen] = useStateT(false);
  // Picking a symbol on a phone should reveal the chart, so close the drawer with it.
  const selectSymbol = (s: string) => { setActive(s); setRailOpen(false); };
  useEffectT(() => {
    if (!railOpen) return;
    const h = (e: any) => { if (e.key === 'Escape') setRailOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [railOpen]);

  const chartApiRef = useRefT<any>(null);
  const stageRef = useRefT<any>(null);
  const idRef = useRefT(1);

  const indicators = indByTf[tf];
  const position = positions[activeSymbol] || null;
  // Effective sizing params: per-symbol override if set, else the REAL account
  // (equity / portfolio value from /api/account), else the worker defaults.
  const sizeParams = useMemo<SizeParams>(() => ({
    accountSize: symbolSizing.accountSize ?? (account.equity || account.portfolioValue || DEFAULT_SIZE_PARAMS.accountSize),
    riskPerTradePercent: symbolSizing.riskPerTradePercent ?? DEFAULT_SIZE_PARAMS.riskPerTradePercent,
    maxPositionValuePercent: symbolSizing.maxPositionValuePercent ?? DEFAULT_SIZE_PARAMS.maxPositionValuePercent,
    minPositionSize: DEFAULT_SIZE_PARAMS.minPositionSize,
  }), [symbolSizing, account]);
  const colors = useMemo(() => chartColors(t), [t.tone, t.accent]);
  const vars = useMemo(() => buildVars(t), [t.tone, t.accent]);
  const meta = useMemo(() => { const m: any = {}; symbols.forEach((s) => { m[s.ticker] = s; }); return m; }, [symbols]);
  const m = meta[activeSymbol] || { name: activeSymbol, base: 0, prevClose: 0, vol: 0, rvol: 0 };

  // The newest toast owns the clear timer: an older one's timer must not wipe a refusal that followed it.
  const toastTimerRef = useRefT<any>(null);
  function flash(msg: string, ms: number = 1800) {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => { toastTimerRef.current = null; setToast(null); }, ms);
  }
  function sendCmd(action: string, symbol: string, params: any = {}) {
    if (MOCK) return;
    watchRefusals(symbol);
    ws.command(action, symbol, params).catch((err: any) => flash('⚠ ' + (err?.message || 'command failed')));
  }

  // The realtime ack only means the command reached the worker; a worker that refuses it answers on the symbol's
  // rr channel (worker command-feedback.ts). Listen for 20 s after each send — any symbol, not only the active one —
  // and on a refusal say why and drop what was drawn on send (the position the buy/short buttons add at once).
  const refusalWatchRef = useRefT<Record<string, { off: () => void; timer: any }>>({});
  const onRefusalRef = useRefT<(f: any) => void>(() => {});
  onRefusalRef.current = (f: any) => {
    flash('⚠ ' + (f.text || (f.symbol + ' command not sent')), 6000);
    getPositions().then((all: any) => {
      const p = all[f.symbol];
      setPositions((prev: any) => {
        if (p) return { ...prev, [f.symbol]: p };
        if (prev[f.symbol]) { const next = { ...prev }; delete next[f.symbol]; return next; }
        return prev;
      });
    }).catch(() => { /* transient — the trade stream / next reconcile heals */ });
  };
  function watchRefusals(symbol: string) {
    const sym = String(symbol || '').trim().toUpperCase();
    if (!sym) return;
    const w = refusalWatchRef.current;
    const cur = w[sym];
    if (cur) clearTimeout(cur.timer);
    const off = cur ? cur.off : subscribeCommandRejections(sym, (f: any) => onRefusalRef.current(f));
    w[sym] = { off, timer: setTimeout(() => { off(); delete w[sym]; }, 20000) };
  }
  useEffectT(() => () => {
    const w = refusalWatchRef.current;
    for (const k of Object.keys(w)) { clearTimeout(w[k]!.timer); w[k]!.off(); }
    refusalWatchRef.current = {};
  }, []);

  // Solo tab: name the browser tab after its symbol so four tiled tabs read
  // like a quote board. Symbol only — a per-tick title would churn the tab
  // strip for nothing (the quote-flood lesson applies to the browser chrome too).
  useEffectT(() => {
    if (SOLO) document.title = activeSymbol + ' · Legend';
  }, [activeSymbol]);

  // ---- initial load: symbols → watchlist + active symbol; positions; account ----
  useEffectT(() => {
    let alive = true;
    getSymbols().then((list) => {
      if (!alive) return;
      setSymbols(list);
      // Watchlist = ALL active symbols (matches Canvas). Was capped at 8.
      setWatch(list.map((s: any) => s.ticker));
      setActive((cur) => cur || (list[0] && list[0].ticker) || '');
    }).catch(() => { /* failed load renders the empty workspace — search still works */ })
      .finally(() => { if (alive) setSymbolsReady(true); });
    getPositions().then((p) => alive && setPositions(p));
    getAccount().then((a) => alive && setAccount({
      equity: a.equity || 0, cash: a.cash || 0, buyingPower: a.buyingPower || 0,
      portfolioValue: a.portfolioValue || 0, lastEquity: a.lastEquity || 0,
    }));
    return () => { alive = false; };
  }, []);

  // ---- live account: the boot fetch above is a one-shot, so equity / Day's P&L in
  // the profile panel froze at page-load. The backend already pushes an `account`
  // frame every second (Alpaca → Mongo sync) — subscribe to it.
  useEffectT(() => subscribeAccount(setAccount), []);

  // ---- live watchlist sync: the backend auto-activates symbols (surge-service
  // spawns), so the active set changes without any user action. The
  // Mongo symbols change stream (`symbols` WS channel) nudges a debounced REST
  // reconcile — frames carry only {symbol, isActivelyTrading}, so the meta
  // (name/price/config) must come from getSymbols() anyway — and a slow poll
  // catches dropped WS frames (documented drop history). Reconcile semantics:
  // append new active symbols (never reorder the user's rows), evict
  // deactivated ones EXCEPT the charted symbol, symbols with an open
  // position/resting order, and just-added tickers racing their server write.
  useEffectT(() => {
    if (MOCK) return;
    // Detached (chart-only) window: no rail, so watchlist membership renders
    // nothing here — skip the WS nudge + 30s getSymbols reconcile. The one
    // thing a trading window still needs from the `symbols` channel is the
    // worker's tradingSide echo (a refused short-flip must snap the toggle
    // back), so keep exactly that mirror for the charted symbol.
    if (SOLO) {
      return subscribeSymbols((u: any) => {
        if (u.symbol !== activeSymbolRef.current) return;
        if (u.tradingSide !== 'short' && u.tradingSide !== 'long') return;
        setSideBySym((m: any) => {
          if (m[u.symbol] === u.tradingSide) return m;
          if (m[u.symbol]) flash('⚠ ' + u.symbol + ' stayed ' + String(u.tradingSide).toUpperCase() + ' — the worker refused the flip');
          return Object.assign({}, m, { [u.symbol]: u.tradingSide });
        });
      });
    }
    let alive = true;
    let inflight = false;
    let debounce: any = null;
    const reconcile = () => {
      if (!alive || inflight) return;
      inflight = true;
      getSymbols().then((list: any[]) => {
        if (!alive || !Array.isArray(list)) return;
        const activeSet = new Set(list.map((s: any) => s.ticker));
        // Seed meta for unseen tickers only — existing entries stay untouched so
        // the `meta` identity (and its key-levels/structure refetch effect) is
        // only disturbed when membership actually changes.
        setSymbols((prev: any[]) => {
          const known = new Set(prev.map((s: any) => s.ticker));
          const adds = list.filter((s: any) => !known.has(s.ticker));
          return adds.length ? [...prev, ...adds] : prev;
        });
        setWatch((prev: string[]) => {
          const now = Date.now();
          const freshOp = (tk: string) => {
            const op = manualOpRef.current[tk];
            return op && now - op.ts < 60_000 ? op : null;
          };
          const isProtected = (tk: string) =>
            tk === activeSymbolRef.current
            || !!positionsRef.current[tk]
            || freshOp(tk)?.on === true;
          const next = prev.filter((tk) => activeSet.has(tk) || isProtected(tk));
          const have = new Set(next);
          for (const s of list) {
            if (have.has(s.ticker)) continue;
            if (freshOp(s.ticker)?.on === false) continue; // just removed by hand — don't bounce back
            next.push(s.ticker);
          }
          return next.length === prev.length && next.every((tk, i) => tk === prev[i]) ? prev : next;
        });
        // An empty terminal auto-loads the first backend symbol (same as boot).
        setActive((cur) => cur || (list[0] && list[0].ticker) || '');
      }).catch(() => { /* transient — the next nudge or poll heals */ })
        .finally(() => { inflight = false; });
    };
    const nudge = () => {
      if (debounce) return; // bursts (a surge spawns several at once) → one fetch
      debounce = setTimeout(() => { debounce = null; reconcile(); }, 400);
    };
    const off = subscribeSymbols((u: any) => {
      // The armed side, BEFORE the membership short-circuit below — which would
      // otherwise swallow exactly the config-only writes this arrives on.
      //
      // This is the WORKER's answer, not the request: it refuses a flip (position
      // open, resting entry, placement in flight, symbol not borrowable) and
      // mirrors back the side actually in force. So unlike the REST seed further
      // down, it OVERRIDES the optimistic local value — otherwise the toggle keeps
      // showing the side the worker rejected and every entry sent from it is
      // refused, silently, until the page is reloaded.
      if (u.tradingSide === 'short' || u.tradingSide === 'long') {
        setSideBySym((m: any) => {
          if (m[u.symbol] === u.tradingSide) return m;
          if (m[u.symbol]) {
            // Only when it CONTRADICTS what the operator clicked. A toggle that
            // snaps back on its own is its own unexplained event.
            flash('⚠ ' + u.symbol + ' stayed ' + String(u.tradingSide).toUpperCase() + ' — the worker refused the flip');
          }
          return Object.assign({}, m, { [u.symbol]: u.tradingSide });
        });
      }
      // The change stream also fires on config writes (preset/trailing toggles) —
      // skip frames that don't change membership for us.
      const has = watchRef.current.includes(u.symbol);
      if (!!u.isActivelyTrading === has) return;
      nudge();
    });
    const poll = setInterval(reconcile, 30000);
    return () => { alive = false; off(); clearInterval(poll); if (debounce) clearTimeout(debounce); };
  }, []);

  // ---- screener feed: seed (REST /api/watchlist) + live (WS `watchlist` channel) ----
  useEffectT(() => {
    if (SOLO) return; // chart-only window: no ScannerList — no seed, no lane, no ping
    let alive = true;
    getScreenerSeed().then((s: any[]) => { if (alive) setSignals(s.slice(0, 40)); });
    const pushSig = (s: any) => {
      setSignals((prev: any[]) => [s, ...prev.filter((x) => x.id !== s.id)].slice(0, 40));
      setSigNewIds([s.id]);
      // Fleet-wide cues sound from the PARENT window only: a detached window
      // renders the same lane, but five windows must not chime five times.
      if (soundOnRef.current && !SOLO) playPing(s.score ?? 0.5);
      setTimeout(() => setSigNewIds((ids) => ids.filter((x) => x !== s.id)), 1200);
    };
    const off = subscribeScreener((s: any) => { if (!sigPausedRef.current) pushSig(s); });
    // MOCK: synthesize surges on a timer (live mode receives them over the WS)
    let mockTimer: any;
    if (MOCK) {
      const tick = () => { mockTimer = setTimeout(() => { if (!sigPausedRef.current) pushSig(mockGenSignal()); tick(); }, 5200 + Math.random() * 4200); };
      tick();
    }
    const fresh = setInterval(() => setSignals((s) => s.slice()), 15000); // keep relative times fresh
    return () => { alive = false; off(); clearTimeout(mockTimer); clearInterval(fresh); };
  }, []);

  // ---- screener SHORT feed: seed (REST /api/watchlist?direction=DOWN) + live
  // (WS `watchlist-short` channel). Structurally identical to the long feed
  // above, with its own state so pausing/clearing one never touches the other.
  // No ping on a flush: the alert sound is the long lane's, and doubling it
  // would make every two-sided move chime twice.
  useEffectT(() => {
    if (SOLO) return; // chart-only window: no short ScannerList
    let alive = true;
    getScreenerShortSeed().then((s: any[]) => { if (alive) setShortSignals(s.slice(0, 40)); });
    const pushSig = (s: any) => {
      setShortSignals((prev: any[]) => [s, ...prev.filter((x) => x.id !== s.id)].slice(0, 40));
      setShortNewIds([s.id]);
      setTimeout(() => setShortNewIds((ids) => ids.filter((x) => x !== s.id)), 1200);
    };
    const off = subscribeScreenerShort((s: any) => { if (!shortPausedRef.current) pushSig(s); });
    // MOCK: synthesize flushes on a timer (live mode receives them over the WS)
    let mockTimer: any;
    if (MOCK) {
      const tick = () => { mockTimer = setTimeout(() => { if (!shortPausedRef.current) pushSig(mockGenShortSignal()); tick(); }, 6800 + Math.random() * 5200); };
      tick();
    }
    const fresh = setInterval(() => setShortSignals((s) => s.slice()), 15000); // keep relative times fresh
    return () => { alive = false; off(); clearTimeout(mockTimer); clearInterval(fresh); };
  }, []);

  // ---- order audio cues: placement / fills / stops / cancels (every symbol) ----
  const orderSeenRef = useRefT<Map<string, string>>(new Map());
  useEffectT(() => {
    return subscribeGlobalOrders((o: any) => {
      if (!soundOnRef.current || !o || !o.orderId) return;
      // Detached windows are silent for order cues — the parent window is the
      // single audio surface (it keeps playing from the background), so a fill
      // is heard exactly once however many windows are open.
      if (SOLO) return;
      const st = String(o.status || '').toLowerCase();
      const seen = orderSeenRef.current;
      const prev = seen.get(o.orderId);
      if (prev === st) return;                 // already cued this transition
      const first = prev === undefined;
      seen.set(o.orderId, st);
      const purpose = String(o.purpose || '').toLowerCase();
      if (st === 'filled') {
        if (purpose === 'take_profit' || purpose === 'target') playProfit();
        else if (purpose.includes('stop')) playStop();
        else playFill();
      } else if (first && (st === 'new' || st === 'accepted' || st === 'pending_new')) {
        playPlaced();
      } else if (st === 'canceled' || st === 'cancelled' || st === 'replaced' || st === 'rejected') {
        playCancel();
      }
    });
  }, []);

  // ---- key levels + S/R for the active symbol ----
  useEffectT(() => {
    if (!activeSymbol) return;
    let alive = true;
    getKeyLevels(activeSymbol, tf, meta[activeSymbol]).then((l) => alive && setLevels(l));
    // Structure rail: seed the level map (mock now; live levels WS wired on contract).
    getStructure(activeSymbol, lastPrice).then((s) => alive && setStructure(s));
    return () => { alive = false; };
  }, [activeSymbol, tf, meta]);

  // ---- live structure updates (worker levels WS: `levels` snapshots + `level-update` deltas) ----
  useEffectT(() => {
    if (!activeSymbol) return;
    return subscribeStructure(activeSymbol, (evt: any) => {
      if (evt.kind === 'snapshot') {
        setStructure(evt.structure); // snapshot is authoritative — replace
      } else if (evt.kind === 'delta') {
        const d = evt.delta;
        setStructure((prev: any) => {
          if (!prev) return prev;
          const patch = (arr: any[]) => arr.map((l) => l.id === d.id ? { ...l, status: d.status, lastTestedAt: d.updatedAt, lastTouchAt: d.updatedAt } : l);
          return { ...prev, supports: patch(prev.supports || []), resistances: patch(prev.resistances || []), flipped: patch(prev.flipped || []) };
        });
      }
    });
  }, [activeSymbol]);

  // Render-ready structure view (heatmap zones + active floor/ceiling).
  // `levelMode` 'cluster' merges nearby levels into confluence bands (the quiet
  // default); 'near' additionally culls zones beyond 6·ATR (declutter); 'all'
  // keeps every level distinct. User-switchable on the map itself, persisted
  // (the same storage key a solo window seeds from).
  const [levelMode, setLevelModeState] = useStateT<string>(() => {
    try { return localStorage.getItem('legend.levelMode') || t.levelMode || 'main'; } catch { return t.levelMode || 'main'; }
  });
  const setLevelMode = (m: string) => {
    setLevelModeState(m);
    try { localStorage.setItem('legend.levelMode', m); } catch { /* ignore */ }
  };
  // Which level KINDS draw (every mode) + the Main-mode budget and the
  // short-candidate tag. Edited from the Levels popover in the chart header;
  // persisted so the choice survives reloads and detached windows.
  const [levelPrefs, setLevelPrefsState] = useStateT<any>(() => {
    try { const s = localStorage.getItem('legend.levelPrefs'); return s ? { ...LEVEL_PREFS_DEFAULT, ...JSON.parse(s) } : LEVEL_PREFS_DEFAULT; } catch { return LEVEL_PREFS_DEFAULT; }
  });
  const setLevelPrefs = (p: any) => {
    setLevelPrefsState(p);
    try { localStorage.setItem('legend.levelPrefs', JSON.stringify(p)); } catch { /* ignore */ }
  };
  // ---- model level reads for the structure rack (vision frames) ----
  // The rack ranks by the model's P(hold) in 'model' mode; frames land once
  // per 1m bar close, wholesale-replace (VisionLayer seq/ts guard). The lane
  // is MV-5 (lib/vision-lane.ts; MV-4 was retired 2026-10-25); the feed stays
  // keyed on it so a future second lane re-keys it and resets the guard —
  // publishers count seq apart.
  const visionLane = useVisionLane();
  const [visionLevels, setVisionLevels] = useStateT<any[]>([]);
  // The same frames, whole, for the entry marks' turning point (MV-5's setups) — the MV-5 lane only: his record was
  // measured against MV-5's setups (journal-builder mv5.ts).
  const [emFrame, setEmFrame] = useStateT<any>(null);
  const visionSeqRef = useRefT<any>({ seq: -1, ts: 0 });
  useEffectT(() => {
    if (!activeSymbol) { setVisionLevels([]); setEmFrame(null); return; }
    let alive = true;
    setVisionLevels([]); setEmFrame(null); visionSeqRef.current = { seq: -1, ts: 0 };
    getVision(activeSymbol, visionLane).then((r: any) => {
      // the saved read whatever its age: the rail keeps MV-5's last read through a halt or a quiet spell
      // (lib/vision-read.ts) — the chart's Vision layer and the verdict ribbon say how old it is
      if (!alive || !r || !r.frame) return;
      if (visionSeqRef.current.seq >= r.frame.seq) return; // a live frame got here first
      visionSeqRef.current = { seq: r.frame.seq, ts: r.frame.ts };
      setVisionLevels(railReads(r.frame));
      if (visionLane === 'mv5') setEmFrame(r.frame);
    }).catch(() => {});
    const off = subscribeVision(activeSymbol, (fr: any) => {
      if (!alive || !fr) return;
      const g = visionSeqRef.current;
      if (fr.seq < g.seq && fr.ts <= g.ts + 5000) return; // seq resets on respawn
      visionSeqRef.current = { seq: fr.seq, ts: fr.ts };
      setVisionLevels(railReads(fr));
      if (visionLane === 'mv5') setEmFrame(fr);
    }, visionLane);
    return () => { alive = false; off(); };
  }, [activeSymbol, visionLane]);

  // The rail's reach read follows the armed side (the header's Long/Short toggle — locked to the position's side
  // while one is open): targets sit above price when long, below when short.
  const railSide = sideBySym[activeSymbol] === 'short' ? 'short' : 'long';
  const srView = useMemo(
    () => (t.showLevels && structure ? structureView(structure, lastPrice, levelMode, visionLevels, levelPrefs, railSide) : null),
    [structure, lastPrice, t.showLevels, levelMode, visionLevels, levelPrefs, railSide],
  );

  // ---- entry marks (EntryMarksLayer): range bars · turning point · tag colour, for the armed side ----
  // His record from the journal (GET /api/journal/entry-record, every 10 minutes); the record the marks shipped with
  // until it answers. The 5m / 15m ranges are measured on 1-minute bars whatever the chart shows: the chart's own on
  // 1m, else useMinuteBars'.
  const emOn = t.entryMarks !== false;
  const [emRecord, setEmRecord] = useStateT<EntryRecord>(ENTRY_RECORD);
  useEffectT(() => {
    if (!emOn) return;
    let alive = true;
    const pull = () => { getEntryRecord().then((r: any) => { const rec = parseRecord(r); if (alive && rec && rec.trades > 0) setEmRecord(rec); }); };
    pull();
    const id = setInterval(pull, 10 * 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [emOn]);
  const minuteBars = useMinuteBars(activeSymbol, emOn && tf !== '1');
  const emRail = srView && srView.zones.length ? (visionLane === 'mv5' ? 36 : 22) : 0;
  let emMarks = null;
  if (emOn && activeSymbol) {
    const src = tf === '1' && chartApiRef.current && chartApiRef.current.getCandles ? chartApiRef.current.getCandles() : null;
    const bars = tf === '1' ? (src && src.symbol === activeSymbol && src.tf === '1' ? src.candles : null) : minuteBars.current;
    emMarks = entryMarksFor(railSide, lastPrice > 0 ? lastPrice : null, bars, emFrame, emRecord, Date.now());
  }

  // ---- supply model for the active symbol (REST hydrate only — a daily history, no WS push) ----
  useEffectT(() => {
    if (!activeSymbol) { setSupplyModel(null); return; }
    let alive = true;
    setSupplyModel(null); // clear the stale card while the new symbol's model loads
    getSupply(activeSymbol).then((r) => { if (alive) setSupplyModel(r); }).catch(() => { if (alive) setSupplyModel(null); });
    return () => { alive = false; };
  }, [activeSymbol]);

  // ---- news + about for the active symbol (REST hydrate + news:<SYM> WS) ----
  useEffectT(() => {
    if (!activeSymbol) { setNewsData(null); setAboutInfo(null); return; }
    let alive = true;
    setNewsData(null); setAboutInfo(null); // never show one symbol's story under another's ticker
    getNews(activeSymbol).then((r: any) => { if (alive) setNewsData(r); }).catch(() => {});
    getAbout(activeSymbol).then((r: any) => { if (alive) setAboutInfo(r); }).catch(() => {});
    const off = subscribeNews(activeSymbol, (n: any) => { if (alive && n) setNewsData(n); });
    return () => { alive = false; off(); };
  }, [activeSymbol]);

  // ---- borrow / short availability for the active symbol (hydrate + borrow:<SYM> WS) ----
  // The broker facts change only when a sweep writes; the card re-derives every
  // displayed number from them and the live quote on every tick.
  useEffectT(() => {
    if (!activeSymbol) { setBorrowData(null); return; }
    let alive = true;
    setBorrowData(null); // never show one symbol's borrow facts under another's ticker
    getBorrow(activeSymbol).then((r: any) => { if (alive) setBorrowData(r); }).catch(() => { if (alive) setBorrowData(null); });
    // A sweep can land while the card is open — most importantly the 04:00 ET
    // run that carries the overnight repricing. Patch the facts in place and
    // keep the hydrate's daily/account/params.
    const off = subscribeBorrow(activeSymbol, (borrow: any) => {
      if (!alive) return;
      setBorrowData((prev: any) => (prev ? { ...prev, borrow } : prev));
    });
    return () => { alive = false; off(); };
  }, [activeSymbol]);

  // ---- held locates, account-wide (poll) ----
  // What we OWN, as distinct from what the desk has left to sell. Workers write
  // `locate:{SYM}` to Redis on every purchase and every adoption; nothing
  // bridges those writes to the WS, so this polls. 20s: a locate is bought at a
  // flip or a press, both of which the operator is watching anyway, and the key
  // lives for hours afterwards.
  //
  // A failed poll KEEPS the last good map. Replacing it with {} would flash
  // "nothing held" on one dropped request, which is the one thing this readout
  // must never say wrongly.
  useEffectT(() => {
    let alive = true;
    const pull = () => {
      getLocates().then((by: any) => { if (alive && by) setHeldLocates(by); }).catch(() => {});
    };
    pull();
    const id = setInterval(pull, 20000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ---- pullback (PBL-v12) signals for the active symbol (REST hydrate + pbl:<SYM> WS) ----
  useEffectT(() => {
    if (!activeSymbol) { setPblReport(null); return; }
    let alive = true;
    setPblReport(null);
    getPbl(activeSymbol).then((r: any) => {
      if (alive && r) setPblReport({ symbol: r.symbol || activeSymbol, signals: r.signals || [] });
    }).catch(() => {});
    const off = subscribePbl(activeSymbol, (frame: any) => {
      if (!alive || !frame) return;
      setPblReport((prev: any) => {
        const k = (s: any) => String(s.ts) + ':' + String(s.action);
        const rest = ((prev && prev.signals) || []).filter((s: any) => k(s) !== k(frame));
        return { symbol: activeSymbol, signals: [frame, ...rest].slice(0, 40) };
      });
    });
    return () => { alive = false; off(); };
  }, [activeSymbol]);

  // ---- live risk/reward AVAILABILITY probe (one REST call per symbol) ----
  // No WS subscription here: rr-update frames arrive up to ~12/min and a
  // Terminal-level setState would re-render the whole tree per frame even with
  // the card closed (the quote-flood lesson). RrCard owns the live feed; this
  // probe only decides whether the "R/R" reopener pill shows for the symbol.
  useEffectT(() => {
    if (!activeSymbol) { setRrAvailable(false); return; }
    let alive = true;
    setRrAvailable(false);
    getRr(activeSymbol).then((r: any) => {
      if (alive) setRrAvailable(!!(r && r.active && r.frame));
    }).catch(() => {});
    return () => { alive = false; };
  }, [activeSymbol]);

  // ---- trade journal for the active symbol (REST hydrate + change-stream re-hydrate) ----
  // The book needs the trades+orders join only the server does, so live frames on
  // trade:{SYM}/orders:{SYM} act as an invalidation tick → debounced refetch (fills
  // arrive in bursts during a scale-out; one fetch per burst is plenty).
  useEffectT(() => {
    if (!activeSymbol) { setJournalBook(null); return; }
    let alive = true;
    let timer: any = null;
    let seq = 0; // out-of-order guard: only the newest issued fetch may write state
    setJournalBook(null);
    const hydrate = () => {
      const my = ++seq;
      // `b &&`: a transient refetch failure must degrade to a slightly-stale book,
      // not unmount the open card and its reopen chip.
      getJournal(activeSymbol).then((b: any) => { if (alive && my === seq && b) setJournalBook(b); }).catch(() => {});
    };
    hydrate();
    const off = subscribeJournal(activeSymbol, () => {
      if (!alive) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(hydrate, 800);
    });
    // Safety-net reconcile — WS frames drop (documented history); same pattern as
    // the position card's 10s heal.
    const reconcile = setInterval(hydrate, 15000);
    return () => { alive = false; if (timer) clearTimeout(timer); clearInterval(reconcile); off(); };
  }, [activeSymbol]);

  // Flip this symbol between long and short. Optimistic locally so the toggle is
  // instant, then the command goes over the WebSocket to the worker, which owns
  // the armed side and persists it. Nothing else in the UI changes yet — no entry
  // path reads this until the short verbs land.
  function setTradeSide(sd: string) {
    const sym = activeSymbol;
    if (!sym) return;
    // Side changes only while FLAT. The button is disabled too, but this is the
    // gate — the worker refuses the same case, so sending it would just fail.
    const openPos = positions[sym];
    if (openPos && openPos.qty > 0) {
      flash('Side locked while ' + sym + ' has an open position — flatten first');
      return;
    }
    const side = sd === 'short' ? 'short' : 'long';
    const prev = sideBySym[sym] || 'long';
    if (prev === side) return;
    setSideBySym((m: any) => Object.assign({}, m, { [sym]: side }));
    if (MOCK) return;
    // NOT sendCmd: that only toasts on failure, leaving the toggle showing a
    // side the worker never accepted. For direction that divergence is the
    // VMAR/WAFU failure mode — the operator stages an entry expecting one way
    // and the engine goes the other. Roll back instead.
    ws.command('set-trading-side', sym, { side }).catch((err: any) => {
      setSideBySym((m: any) => Object.assign({}, m, { [sym]: prev }));
      flash('⚠ side unchanged (' + (err?.message || 'command failed') + ')');
    });
  }

  // ---- per-symbol exit config (target preset + trailing/runner mode) ----
  useEffectT(() => {
    if (!activeSymbol) return;
    let alive = true;
    getSymbolConfig(activeSymbol).then((c: any) => {
      if (!alive) return;
      setCfgPreset((c.targetPreset || 'NONE').toLowerCase());
      setCfgTrailing(c.trailingStopEnabled ?? false);
      // Seed only if the operator hasn't picked a source this session: the fetch
      // snapshot predates any click made while it was in flight, and clobbering
      // the optimistic entry would make the NEXT toggle send the wrong source.
      setTrailSrcBySym((prev: any) => prev[activeSymbol]
        ? prev
        : Object.assign({}, prev, { [activeSymbol]: c.trailingIndicator || 'levels' }));
      // Same optimistic guard as the trailing source: if the operator flipped
      // side while this fetch was in flight, the snapshot predates the click and
      // seeding it would silently revert them to long.
      setSideBySym((prev: any) => prev[activeSymbol]
        ? prev
        : Object.assign({}, prev, { [activeSymbol]: c.tradingSide === 'short' ? 'short' : 'long' }));
      setSymbolSizing({
        accountSize: c.accountSize,
        riskPerTradePercent: c.riskPerTradePercent,
        maxPositionValuePercent: c.maxPositionValuePercent,
      });
    });
    return () => { alive = false; };
  }, [activeSymbol]);

  // ---- live 1m indicator snapshot (trail-source values on the position card) ----
  // Worker publishes per completed 1m bar; REST history seeds the display so the
  // card isn't blank for up to a minute after a symbol switch. Live frames win.
  useEffectT(() => {
    if (!activeSymbol) return;
    let alive = true;
    setIndSnap(null);
    getIndicators(activeSymbol, '1').then((rows: any[]) => {
      if (!alive || !rows.length) return;
      const last = rows[rows.length - 1].values || {};
      setIndSnap((prev: any) => prev || { ...last, seeded: true });
    });
    const off = subscribeIndicators(activeSymbol, (s: any) => { if (alive) setIndSnap(s); });
    return () => { alive = false; off(); };
  }, [activeSymbol]);

  // seed lastPrice from meta when symbol changes (until the first tick/quote arrives)
  useEffectT(() => {
    if (meta[activeSymbol]) setLastPrice(meta[activeSymbol].last ?? meta[activeSymbol].base ?? 0);
  }, [activeSymbol, meta]);

  // Reconcile a change-stream trade frame's entry chips with what's on screen.
  // entryOrders[] is embedded in the trade doc itself, so WS frames carry it even
  // though they never attach the orders collection — trust the freshly-mapped
  // entries whenever it's present so a canceled entry can clear its chip over WS
  // (FRTT 2026-08-11: the old hasOrders-only gate pinned canceled chips until a
  // REST heal that only the active symbol ever gets). Two carve-outs:
  // - midSequence: a fill writes the 'filled' element BEFORE the position doc
  //   write, and a reprice writes 'replaced' BEFORE appending the replacement
  //   row — those intermediate frames (last row filled/replaced, mapped entries
  //   empty) keep the previous chips instead of evicting the position for a
  //   beat. Genuine cancels end on a 'canceled' row and do evict.
  // - optimistic: a just-placed local chip (id 'e<n>') survives doc-driven
  //   remaps for 10s (deduped by kind+price) until its appendEntryOrder frame
  //   lands, so the click doesn't blink.
  const reconcileEntries = (doc: any, mapped: any[] | null, prevEntries: any[]): { entries: any[]; midSequence: boolean } => {
    const rows: any[] = Array.isArray(doc.entryOrders) ? doc.entryOrders : [];
    const lastRow = rows.length ? rows[rows.length - 1] : null;
    const midSequence = !Array.isArray(doc.orders) && lastRow != null
      && (lastRow.status === 'filled' || lastRow.status === 'replaced');
    if (mapped == null) return { entries: prevEntries, midSequence };
    let entries = (midSequence && mapped.length === 0) ? prevEntries : mapped;
    const optimistic = prevEntries.filter((e: any) =>
      /^e\d+$/.test(String(e.id)) && Date.now() - (e.ts ?? 0) < 10000
      && !entries.some((n: any) => n.kind === e.kind && Math.abs((n.px ?? 0) - (e.px ?? 0)) < 1e-6));
    if (optimistic.length) entries = [...entries, ...optimistic];
    return { entries, midSequence };
  };

  // ---- live: global trade stream → watchlist position markers ----
  useEffectT(() => {
    return subscribeGlobalTrades((doc: any) => {
      setPositions((prev: any) => {
        const next = { ...prev };
        const pos = mapTradeToPosition(doc);
        const hasOrders = Array.isArray(doc.orders);
        const hasEntryRows = Array.isArray(doc.entryOrders) && doc.entryOrders.length > 0;
        const mapped = (hasOrders || hasEntryRows) ? (pos?.entries || []) : null;
        const { entries, midSequence } = reconcileEntries(doc, mapped, prev[doc.symbol]?.entries ?? []);
        if (!pos || doc.status !== 'open' || (pos.qty <= 0 && entries.length === 0 && !midSequence)) { delete next[doc.symbol]; return next; }
        next[doc.symbol] = hasOrders ? pos : { ...pos, targets: prev[doc.symbol]?.targets ?? [], entries };
        return next;
      });
    });
  }, []);

  // ---- live: per-watchlist-symbol trade prints → row prices ----
  // Use the market:<SYM> trade channel (real last price). Quotes are often absent
  // outside RTH while trades still flow; trades are also what Alpaca shows as "last".
  // Prints buffer into a ref and flush to state on a 150ms clock: one render per
  // flush instead of one per print. A surge across the watchlist used to queue a
  // full-tree render per WS message (each message is its own task, so React can't
  // batch them) and starved click dispatch for seconds.
  const rowBufRef = useRefT<Record<string, { last: number; first: number }>>({});
  useEffectT(() => {
    // Chart-only window: row prices render nothing (no watchlist / movers).
    // This fan-out is one trade channel PER WATCHED SYMBOL — the widest
    // subscription in the app — so detached windows must not multiply it.
    if (SOLO) return;
    const buf = rowBufRef.current;
    const offs = watch.map((sym) => subscribeTrades(sym, (t: any) => {
      if (t.price == null) return;
      const cur = buf[sym];
      buf[sym] = { last: t.price, first: cur ? cur.first : t.price };
    }));
    const flush = setInterval(() => {
      const syms = Object.keys(buf);
      if (!syms.length) return;
      const batch: Record<string, { last: number; first: number }> = {};
      for (const sym of syms) { batch[sym] = buf[sym]; delete buf[sym]; }
      setRowPrices((prev: any) => {
        let changed = false;
        const next: any = { ...prev };
        for (const sym of Object.keys(batch)) {
          const b = batch[sym];
          const cur = prev[sym];
          const initial = cur?.initial ?? b.first;
          if (!cur || cur.last !== b.last || cur.initial !== initial) {
            next[sym] = { last: b.last, initial };
            changed = true;
          }
        }
        return changed ? next : prev; // identical prices → no render
      });
    }, 150);
    return () => { offs.forEach((o) => o()); clearInterval(flush); };
  }, [watch.join(',')]);

  // ---- live: per-watchlist-symbol ladder signals → row illumination + sound ----
  // The server only publishes the loud (backtest-proven) states as
  // `ladder-signal`, so any frame received is worth surfacing. Mirrors the
  // LadderCard alarm but on the side list, so a signal is caught without that
  // symbol's card open. Sound is suppressed for the active symbol when its
  // ladder card is already blipping (avoids a double cue).
  useEffectT(() => {
    // Chart-only window: the sweep only lights watchlist rows (not rendered)
    // and its sound is parent-only anyway — skip the per-symbol channels.
    if (SOLO) return;
    const timers = ladAlarmTimersRef.current;
    const offs = watch.map((sym) => subscribeLadder(sym, (f: any) => {
      if (f.type !== 'ladder-signal') return;
      const kind = ladderSignalKind(f.state);
      if (!kind) return;
      setLadderAlarms((prev) => ({ ...prev, [sym]: kind }));
      // sound is parent-only (see the screener lane); the row still lights here
      if (soundOnRef.current && !SOLO && !(sym === activeSymbolRef.current && ladVisibleRef.current)) {
        playLadderAlarm(kind === 'flip');
      }
      if (timers[sym]) clearTimeout(timers[sym]);
      timers[sym] = setTimeout(() => {
        setLadderAlarms((prev) => { const n = { ...prev }; delete n[sym]; return n; });
        delete timers[sym];
      }, 3000);
    }));
    return () => {
      offs.forEach((o) => o());
      Object.values(timers).forEach((t) => clearTimeout(t as any));
      ladAlarmTimersRef.current = {};
    };
  }, [watch.join(',')]);

  // ---- live: active symbol trade/order streams → position detail ----
  useEffectT(() => {
    if (!activeSymbol) return;
    // Re-hydrate the active position from REST — and EVICT it when the API no
    // longer returns the symbol (trade closed). The old `p ? merge : keep-prev`
    // pinned a closed position (and its stop line) on screen until a full page
    // reload whenever the single WS close frame was dropped: removal depended
    // entirely on that one frame, and this fallback could never heal it.
    const rehydrateActive = () => {
      getPositions().then((all: any) => {
        const p = all[activeSymbol];
        setPositions((prev: any) => {
          if (p) return { ...prev, [activeSymbol]: p };
          if (prev[activeSymbol]) { const next = { ...prev }; delete next[activeSymbol]; return next; }
          return prev;
        });
      }).catch(() => { /* transient — next reconcile heals */ });
    };
    const unsub = subscribeSymbolStreams(activeSymbol,
      (doc: any) => {
        if (doc.symbol && doc.symbol !== activeSymbol) return;
        setPositions((prev: any) => {
          const next = { ...prev };
          const pos = mapTradeToPosition(doc);
          const hasOrders = Array.isArray(doc.orders);
          // Same entryOrders[]-trust rule as the global-stream handler above:
          // canceled entries must clear over WS, not wait for the REST heal.
          const hasEntryRows = Array.isArray(doc.entryOrders) && doc.entryOrders.length > 0;
          const mapped = (hasOrders || hasEntryRows) ? (pos?.entries || []) : null;
          const { entries, midSequence } = reconcileEntries(doc, mapped, prev[activeSymbol]?.entries ?? []);
          if (!pos || doc.status !== 'open' || (pos.qty <= 0 && entries.length === 0 && !midSequence)) { delete next[activeSymbol]; return next; }
          next[activeSymbol] = hasOrders ? pos : { ...pos, targets: prev[activeSymbol]?.targets ?? [], entries };
          return next;
        });
      },
      // An order changed (target/entry added, cancelled, filled) → re-hydrate the
      // active position from REST so the order-derived pills reflect it. Close
      // orders emit several events, so this now doubles as a second, multi-shot
      // close-removal path independent of the trade-stream close frame.
      rehydrateActive);
    // Safety-net reconcile: even if EVERY WS frame for the close is dropped
    // (documented drop history — see the never-give-up reconnect fix), the
    // position card self-heals within ~10s instead of requiring a reload.
    const reconcile = setInterval(rehydrateActive, 10000);
    return () => { (unsub as any)?.(); clearInterval(reconcile); };
  }, [activeSymbol]);

  // ---- live price from chart (+ trailing-stop logic) ----
  // The chart updates imperatively (lightweight-charts); pushing every tick into React
  // state fired a render per WS message on a fast tape. Coalesce BOTH lastPrice and
  // the trailing-stop ratchet to one update per animation frame. Per-tick work is
  // pure ref math; the ratchet runs on the intra-frame price extreme (hi for longs,
  // lo for shorts), which lands the stop on exactly the price a per-tick ratchet
  // would have reached — a monotone ratchet only ever sees the extreme. The client
  // trail line is display-only anyway: the worker owns the real trailing stop.
  const lastPriceRef = useRefT(0);
  const priceExtRef = useRefT<{ hi: number; lo: number } | null>(null);
  const priceRafRef = useRefT<number | null>(null);
  useEffectT(() => () => { if (priceRafRef.current != null) cancelAnimationFrame(priceRafRef.current); }, []);
  const onPrice = (p: number) => {
    lastPriceRef.current = p;
    const ext = priceExtRef.current;
    if (!ext) priceExtRef.current = { hi: p, lo: p };
    else { if (p > ext.hi) ext.hi = p; if (p < ext.lo) ext.lo = p; }
    if (priceRafRef.current == null) {
      priceRafRef.current = requestAnimationFrame(() => {
        priceRafRef.current = null;
        const e = priceExtRef.current;
        priceExtRef.current = null;
        setLastPrice(lastPriceRef.current);
        if (!e) return;
        const sym = activeSymbolRef.current;
        setPositions((prev: any) => {
          const pos = prev[sym];
          if (!pos || !pos.trailing || !pos.stop || pos.qty <= 0) return prev;
          const px = pos.side === 'long' ? e.hi : e.lo;
          const prec = px < 1 ? 4 : 2;
          const dist = pos.trailDist || Math.abs(px - pos.stop.px);
          let ns = pos.stop.px;
          if (pos.side === 'long') ns = Math.max(pos.stop.px, +(px - dist).toFixed(prec));
          else ns = Math.min(pos.stop.px, +(px + dist).toFixed(prec));
          if (ns === pos.stop.px) return prev;
          return Object.assign({}, prev, { [sym]: Object.assign({}, pos, { stop: Object.assign({}, pos.stop, { px: ns }) }) });
        });
      });
    }
  };

  // Trading actions accept an explicit symbol/price (callers off the active chart can
  // trade their own symbol; they default to the main pane's active symbol.
  function applyPreset(key: string, sym: string = activeSymbol) {
    const def = TARGET_PRESETS.find((x) => x.key === key);
    if (!def) return;
    // Changing the preset only updates the *configured* preset on the server — it does
    // NOT repost live target orders. The backend applies the new preset to the next
    // order/repost. So don't fabricate targets here: that showed phantom targets that
    // weren't actually resting orders. Real targets flow back via the order stream
    // (mapTradeToPosition when doc.orders is present / the REST re-hydrate on order change).
    if (sym === activeSymbol) setCfgPreset(key);
    setPositions((prev: any) => {
      const p = prev[sym]; if (!p) return prev;
      return Object.assign({}, prev, { [sym]: Object.assign({}, p, { preset: key }) });
    });
    sendCmd('set-target-preset', sym, { targetPreset: key.toUpperCase() });
    flash('Exit preset → ' + def.label + (sym === activeSymbol ? '' : ' · ' + sym));
  }

  function toggleTrailing(sym: string = activeSymbol, px: number = lastPrice) {
    const pos0 = positions[sym];
    const cur = pos0 && pos0.trailing != null ? !!pos0.trailing : (sym === activeSymbol ? cfgTrailing : false);
    const willOn = !cur;
    const src = trailSrcBySym[sym] || 'levels';
    if (sym === activeSymbol) setCfgTrailing(willOn);
    setPositions((prev: any) => {
      const p = prev[sym]; if (!p || p.qty <= 0) return prev;
      const prec = px < 1 ? 4 : 2;
      let stop = p.stop;
      let trailDist = p.trailDist;
      if (willOn) {
        if (!stop) stop = { id: 'st', px: +((p.side === 'long' ? px * 0.97 : px * 1.03)).toFixed(prec), qty: p.qty };
        trailDist = Math.abs(px - stop.px);
      }
      return Object.assign({}, prev, { [sym]: Object.assign({}, p, { trailing: willOn, stop, trailDist }) });
    });
    sendCmd('set-trailing-stop', sym, { trailingStopEnabled: willOn, trailingIndicator: src });
    flash('Trailing stop ' + (willOn ? 'on' : 'off') + (sym === activeSymbol ? '' : ' · ' + sym));
  }

  // Pick what the trail follows. SOURCE-ONLY command — set-trailing-stop is a
  // partial update server-side, so this must not carry the enabled bit: the
  // client's knowledge of it is unreliable (hydration gaps on symbol switch,
  // solo windows whose position docs never carry `trailing`), and echoing a
  // stale false here would silently disarm a live trail. Sent even while OFF so
  // the choice is recorded for the next enable and for respawned workers.
  function applyTrailSource(key: string, sym: string = activeSymbol) {
    const def = TRAIL_SOURCES.find((x) => x.key === key);
    if (!def) return;
    setTrailSrcBySym((prev: any) => Object.assign({}, prev, { [sym]: key }));
    sendCmd('set-trailing-stop', sym, { trailingIndicator: key });
    flash('Trail source → ' + def.label + (sym === activeSymbol ? '' : ' · ' + sym));
  }

  function editOrder(kind: string, id: string, px: number) {
    setPositions((prev: any) => {
      const p = prev[activeSymbol]; if (!p) return prev;
      const np = Object.assign({}, p);
      if (kind === 'target') np.targets = p.targets.map((x: any) => x.id === id ? Object.assign({}, x, { px }) : x);
      else if (kind === 'stop') np.stop = Object.assign({}, p.stop, { px });
      else if (kind === 'entry') np.entries = p.entries.map((x: any) => x.id === id ? Object.assign({}, x, { px }) : x);
      return Object.assign({}, prev, { [activeSymbol]: np });
    });
  }
  const onOrderDrag = (k: string, id: string, px: number) => editOrder(k, id, px);
  const onOrderCommit = (k: string, id: string, px: number) => {
    editOrder(k, id, px);
    if (k === 'stop') sendCmd('move-stop', activeSymbol, { newStopPrice: px });
    else sendCmd('update-order', activeSymbol, { orderId: id, newPrice: px });
    flash((k === 'stop' ? 'Stop' : k === 'target' ? 'Target' : 'Order') + ' moved → $' + fmtPx4(px));
  };
  function cancelOrder(kind: string, id: string) {
    setPositions((prev: any) => {
      const p = prev[activeSymbol]; if (!p) return prev;
      const np = Object.assign({}, p);
      if (kind === 'target') np.targets = p.targets.filter((x: any) => x.id !== id);
      else if (kind === 'stop') np.stop = null;
      else if (kind === 'entry') np.entries = p.entries.filter((x: any) => x.id !== id);
      return Object.assign({}, prev, { [activeSymbol]: np });
    });
    if (kind === 'stop') sendCmd('cancel-stop', activeSymbol, { reason: 'manual' });
    else sendCmd('cancel-order', activeSymbol, { orderId: id });
    flash((kind === 'stop' ? 'Stop' : kind === 'entry' ? 'Entry order' : 'Target') + ' cancelled');
  }

  function onChartClick({ price, x, y }: any) {
    const kind = price < lastPrice ? 'dip' : 'break';
    setPopover({ kind, price, x, y, stageH: stageRef.current ? stageRef.current.clientHeight : 600 });
  }
  // Stage an order straight off a structure level (click in the map gutter).
  function stageFromLevel(price: number) {
    const api = chartApiRef.current, stage = stageRef.current;
    const y = api ? api.priceToY(price) : null;
    setPopover({ kind: price < lastPrice ? 'dip' : 'break', price, x: 150, y: (y == null ? 220 : y), stageH: stage ? stage.clientHeight : 600 });
  }
  function repositionPopover(clientX: number, clientY: number) {
    const rect = stageRef.current.getBoundingClientRect();
    const y = clientY - rect.top; const x = clientX - rect.left;
    const api = chartApiRef.current; if (!api) return;
    const price = api.yToPrice(y); if (price == null) return;
    setPopover({ kind: price < lastPrice ? 'dip' : 'break', price, x, y, stageH: rect.height });
  }
  // The three chart classifiers above are GEOMETRIC — 'dip' means below market,
  // 'break' means above — and geometry does not change with side. So none of
  // them is side-aware; the verb is resolved once, here, which is why there is
  // no way to update one classifier and forget another (services.md §4: "miss
  // one copy and the first click is right and the reposition click reverts to
  // long semantics").
  //
  //   long  + below = buy into weakness      → buy-dip
  //   long  + above = buy the breakout       → buy-on-break
  //   short + above = sell into strength     → short-on-pop
  //   short + below = short the breakdown    → short-on-dip
  const ENTRY_VERB: Record<string, Record<string, string>> = {
    long: { dip: 'buy-dip', break: 'buy-on-break' },
    short: { dip: 'short-on-dip', break: 'short-on-pop' },
  };
  const ENTRY_LABEL: Record<string, Record<string, string>> = {
    long: { dip: 'Buy Dip', break: 'Buy Break' },
    short: { dip: 'Short Breakdown', break: 'Short Pop' },
  };

  function placeOrder(kind: string, price: number, bucket: any) {
    const shares = bucketShares(bucket.key, price, sizeParams);
    const id = 'e' + (idRef.current++);
    const side = sideBySym[activeSymbol] || 'long';
    setPositions((prev: any) => {
      const p = prev[activeSymbol] || { side, qty: 0, avg: 0, targets: [], stop: null, entries: [] };
      const np = Object.assign({}, p, { entries: [...(p.entries || []), { id, kind, px: price, bucket: bucket.key, bucketName: bucket.name, shares, pending: true, ts: Date.now() }] });
      return Object.assign({}, prev, { [activeSymbol]: np });
    });
    setPopover(null);
    sendCmd(ENTRY_VERB[side]![kind]!, activeSymbol, { price, sizing: sizingFor(bucket.key) });
    flash(ENTRY_LABEL[side]![kind]! + ' ' + bucket.name + ' resting @ $' + fmtPx4(price));
    setTimeout(() => setPositions((prev: any) => {
      const p = prev[activeSymbol]; if (!p) return prev;
      return Object.assign({}, prev, { [activeSymbol]: Object.assign({}, p, { entries: p.entries.map((e: any) => e.id === id ? Object.assign({}, e, { pending: false }) : e) }) });
    }), 700);
  }

  function buyNow(bucket: any, sym: string = activeSymbol, px: number = lastPrice) {
    const shares = bucketShares(bucket.key, px, sizeParams);
    const side = sideBySym[sym] || 'long';
    setPositions((prev: any) => {
      const p = prev[sym];
      // Only average into a position on the SAME side. An opposite-side fill is
      // a new position, not an add — treating it as an add would blend a short
      // entry into a long average and misreport P&L on both.
      if (p && p.qty > 0 && p.side === side) {
        const newQty = p.qty + shares;
        const newAvg = (p.avg * p.qty + px * shares) / newQty;
        return Object.assign({}, prev, { [sym]: Object.assign({}, p, { qty: newQty, avg: +newAvg.toFixed(4) }) });
      }
      return Object.assign({}, prev, { [sym]: { side, qty: shares, avg: +px.toFixed(4), targets: (p && p.targets) || [], stop: (p && p.stop) || null, entries: (p && p.entries) || [] } });
    });
    sendCmd(side === 'short' ? 'short-now' : 'buy-now', sym, { sizing: sizingFor(bucket.key) });
    flash((side === 'short' ? 'Shorted ' : 'Bought ') + bucket.name + ' (' + fmtQty(shares) + ' sh) @ mkt' + (sym === activeSymbol ? '' : ' · ' + sym));
  }
  function closeNow(sym: string = activeSymbol) {
    // Send the close to the worker; reconcile from the trade change-stream (don't
    // optimistically wipe — that falsely shows "closed" if the close can't fill).
    sendCmd('close-now', sym, { reason: 'manual' });
    flash('Close order sent → market' + (sym === activeSymbol ? '' : ' · ' + sym));
  }

  function sellPercent(pct: number, sym: string = activeSymbol) {
    // Trim a % of the CURRENT remaining position. Worker resolves the qty live and
    // fills marketable; reconcile from the trade change-stream like closeNow.
    sendCmd('sell-percent', sym, { percent: pct, reason: 'manual' });
    flash('Sell ' + pct + '% sent → market' + (sym === activeSymbol ? '' : ' · ' + sym));
  }

  function toggleInd(key: string) { setIndByTf((prev: any) => Object.assign({}, prev, { [tf]: Object.assign({}, prev[tf], { [key]: !prev[tf][key] }) })); }

  const dragId = useRefT<any>(null);
  const reorder = {
    get draggingId() { return dragId.current; },
    onStart: (id: string) => { dragId.current = id; },
    onEnd: () => { dragId.current = null; },
    onDrop: (targetId: string) => {
      const from = dragId.current; if (!from || from === targetId) return;
      setWidgets((ws2) => { const arr = [...ws2]; const fi = arr.findIndex((w) => w.id === from); const ti = arr.findIndex((w) => w.id === targetId); const [mv] = arr.splice(fi, 1); arr.splice(ti, 0, mv); return arr; });
      dragId.current = null;
    },
  };
  const rowDrag = useRefT<any>(null);
  const rowReorder = {
    get dragging() { return rowDrag.current; },
    onStart: (tk: string) => { rowDrag.current = tk; },
    onEnd: () => { rowDrag.current = null; },
    onDrop: (tk: string) => { const from = rowDrag.current; if (!from || from === tk) return; setWatch((w) => { const a = [...w]; const fi = a.indexOf(from), ti = a.indexOf(tk); a.splice(fi, 1); a.splice(ti, 0, from); return a; }); rowDrag.current = null; },
  };

  function rowData(ticker: string) {
    const mm = meta[ticker] || { name: ticker, base: 0, prevClose: 0, vol: 0, rvol: 0 };
    const rp = rowPrices[ticker];
    const live = ticker === activeSymbol ? lastPrice : (rp?.last ?? mm.last ?? mm.base ?? 0);
    const prev = mm.prevClose ?? mm.base ?? rp?.initial ?? live;
    const chg = prev ? (live - prev) / prev : 0;
    const pos = positions[ticker];
    return { ticker, name: mm.name, last: live, chg, vol: mm.vol || 0, rvol: mm.rvol || 0, posSide: pos && pos.qty > 0 ? pos.side : null };
  }
  const watchRows = watch.map(rowData);
  const moverRows = symbols.map((s) => s.ticker).sort((a, b) => (meta[b]?.chg || 0) - (meta[a]?.chg || 0)).slice(0, 8).map(rowData);

  function addWidget(type: string) { setWidgets((ws2) => [...ws2, { id: 'w' + (idRef.current++), type }]); }
  function removeWidget(id: string) { setWidgets((ws2) => ws2.filter((w) => w.id !== id)); }

  // Accepts a ticker string (movers) or a full symbol object (search results).
  // Seeds the symbol into `symbols` so meta/rowData know its name/price — without
  // this a searched symbol has no meta entry and selecting it would crash the header.
  function addToWatch(sym: any) {
    const tk = typeof sym === 'string' ? sym : sym?.ticker;
    if (!tk) return;
    const obj = typeof sym === 'string' ? null : sym;
    if (obj) setSymbols((prev: any[]) => prev.some((s) => s.ticker === tk) ? prev : [...prev, obj]);
    setWatch((w) => (w.includes(tk) ? w : [...w, tk]));
    manualOpRef.current[tk] = { ts: Date.now(), on: true }; // shield from the live-sync reconciler until the server write lands
    // First symbol added into an empty terminal loads the chart immediately.
    setActive((cur) => cur || tk);
    setWatchlist(tk, true, obj?.name || meta[tk]?.name);
  }
  function removeFromWatch(tk: string) {
    manualOpRef.current[tk] = { ts: Date.now(), on: false }; // keep the reconciler from re-adding it
    setWatch((w) => w.filter((x) => x !== tk));
    setWatchlist(tk, false);
  }

  // Click a screener signal → add the symbol to the watchlist and load it on the chart.
  function loadSignal(sig: any) {
    addToWatch({ ticker: sig.symbol, name: sig.symbol, last: sig.price, base: sig.price, prevClose: sig.price, vol: 0, rvol: sig.rvol });
    selectSymbol(sig.symbol);
  }

  // Click a heat chip / "last rotation" → chart it. Same watchlist-add + select
  // lane as a screener signal: the event's price seeds base/prevClose so an
  // out-of-watchlist symbol doesn't render a fabricated day-change (base 0 →
  // +full-price green). Known tradeoff shared with the other lanes: the change
  // column reads 0.00% until real meta lands.
  function loadHeat(s: any) {
    addToWatch({ ticker: s.symbol, name: s.symbol, last: s.price, base: s.price, prevClose: s.price, vol: 0, rvol: s.rvol || 0 });
    selectSymbol(s.symbol);
  }

  // Click a news row → chart it WITHOUT the board-add lane. Every other rail
  // click (signal/heat/fire) rides addToWatch → setWatchlist → PUT /symbols,
  // which ACTIVATES the symbol and spawns a worker — right for curated
  // momentum events, wrong for browsing a headline feed (each click would
  // burn a 2h board lease and a pod). Seed a placeholder meta row so the
  // header can render, select, then enrich name/price via the same mapped
  // search the watchlist uses. Historical bars hydrate for every universe
  // symbol; live tape starts only if the user then adds it to the board.
  function loadNews(r: any) {
    const tk = r.symbol;
    if (!tk) return;
    setSymbols((prev: any[]) => prev.some((s) => s.ticker === tk)
      ? prev
      : [...prev, { ticker: tk, name: tk, last: 0, base: 0, prevClose: 0, vol: 0, rvol: 0 }]);
    selectSymbol(tk);
    searchSymbols(tk).then((res: any[]) => {
      const hit = (res || []).find((s: any) => s.ticker === tk);
      // Replace only the placeholder — never clobber an established meta row.
      if (hit) setSymbols((prev: any[]) => prev.map((s) => (s.ticker === tk && !s.last ? hit : s)));
    }).catch(() => { /* placeholder stays; chart still renders from bars */ });
  }

  function signOut() {
    if (MOCK) { try { location.reload(); } catch {} return; }
    ws.disconnect();
    fbSignOut();
  }

  useEffectT(() => {
    if (!popover) return;
    const h = (e: any) => { if (stageRef.current && !stageRef.current.contains(e.target)) setPopover(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [popover]);

  // Splash only while the symbol list is genuinely in flight. An empty (or failed)
  // load renders the workspace with an empty-state main pane instead — the old
  // `!activeSymbol` gate deadlocked an empty terminal on this splash with no way
  // to reach the watchlist search and add the first symbol.
  if (!activeSymbol && !symbolsReady) {
    return React.createElement('div', { className: 'app', style: vars },
      React.createElement('div', { style: { color: 'var(--t3)', font: '13px var(--sans)', padding: 24 } }, 'Loading symbols…'));
  }
  const hasSymbol = !!activeSymbol;

  const summary = accountSummary(positions, activeSymbol, lastPrice, meta, account);

  return React.createElement('div', {
    className: 'app' + (SOLO ? ' solo' : ''), style: vars,
    'data-density': t.density, 'data-pillstyle': t.pillStyle, 'data-cardstyle': t.cardStyle,
    'data-popover': t.popoverLayout, 'data-rowstyle': t.rowStyle,
  },
    React.createElement('div', { className: 'topbar' },
      SOLO ? null : React.createElement('button', {
        className: 'rail-toggle', onClick: () => setRailOpen((v) => !v),
        'aria-label': 'Toggle workspace', 'aria-expanded': railOpen,
      }, React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' }, React.createElement('path', { d: 'M4 6h16M4 12h16M4 18h16' }))),
      React.createElement('div', { className: 'brand', title: 'Momentum' }, React.createElement('span', { className: 'mark' })),
      // SIM mode (VITE_SIM=1, compile-time — prod keeps only the else-branches):
      // no green LIVE chip on a replayed day, and the SimBar (amber SIM badge +
      // sim-clock date/time + play/pause/skip) takes the clock's place. It owns
      // its own tick, so it never re-renders this tree.
      import.meta.env.VITE_SIM === '1' ? null : React.createElement('div', { className: 'status-chip live-chip' }, React.createElement('span', { className: 'dot-live' }), MOCK ? 'LIVE · SIM' : 'LIVE'),
      React.createElement('div', { className: 'status-chip mobile-hide', style: { borderColor: 'transparent', background: 'transparent', color: 'var(--t4)' } }, 'Paper · Equities'),
      // Seeded with the real ET time so the very first paint is honest; App's 1 Hz
      // effect takes over the textContent from there.
      import.meta.env.VITE_SIM === '1'
        ? React.createElement(SimBar)
        : React.createElement('div', { className: 'clock status-chip mono', id: 'clock' }, etClock(Date.now()) + ' ET'),
      React.createElement(AccountChip, { summary, user, onSignOut: signOut, onOpenJournal: () => setJournal({ open: true, date: null, trade: null, view: null }), onOpenAgent: () => setAgent({ open: true, date: null }) }),
      // Portaled to <body>: position:fixed must not be trapped by a transformed ancestor; the wrapper carries the
      // terminal's accent and up/down colours.
      journal.open ? createPortal(React.createElement('div', { style: vars },
        React.createElement(React.Suspense, { fallback: null },
          React.createElement(JournalApp, { user, initialDate: journal.date, initialTrade: journal.trade, initialView: journal.view, onClose: () => setJournal({ open: false, date: null, trade: null, view: null }) }))), document.body) : null,
      agent.open ? createPortal(React.createElement('div', { style: vars },
        React.createElement(React.Suspense, { fallback: null },
          React.createElement(AgentApp, { user, initialDate: agent.date, onClose: () => setAgent({ open: false, date: null }) }))), document.body) : null),

    // Rotation radar — owns its own `heat:*` subscriptions and state, so the
    // 1 Hz leaderboard re-renders only the strip, never this tree. Chip click =
    // the signal-load lane (watchlist-add seeded at the event price + select).
    // soundOn gates the rotation beep under the master toggle, additively with
    // the strip's own mute. `order` pins chips to the user's watchlist order —
    // heat shows as intensity in place, not position churn.
    // Detached (chart-only) window: no heat strip, no rail — the chart column
    // owns the window. Everything INSIDE the chart column is identical.
    SOLO ? null : PP('HeatStrip', React.createElement(HeatStrip, { onSelect: loadHeat, soundOn, order: watch })),

    React.createElement('div', { className: 'workspace' },
      SOLO ? null : React.createElement('div', { className: 'rail-scrim' + (railOpen ? ' show' : ''), onClick: () => setRailOpen(false) }),
      SOLO ? null : PP('Rail', React.createElement('div', { className: 'rail' + (railOpen ? ' open' : '') },
        React.createElement('div', { className: 'rail-head' },
          React.createElement('span', { className: 'title' }, 'Workspace'),
          React.createElement('div', { className: 'spacer' }),
          React.createElement('div', { style: { position: 'relative' } },
            React.createElement('button', { className: 'icon-btn', onClick: () => setAddOpen((v) => !v), title: 'Add widget' }, Icon.plus()),
            addOpen ? React.createElement(AddWidgetMenu, { onAdd: addWidget, onClose: () => setAddOpen(false), present: widgets.map((w) => w.type) }) : null)),
        React.createElement('div', { className: 'rail-body' },
          widgets.map((w) => React.createElement(WidgetCard, {
            key: w.id, w, collapsed: !!collapsed[w.id],
            onToggle: () => setCollapsed((cc: any) => Object.assign({}, cc, { [w.id]: !cc[w.id] })),
            onClose: () => removeWidget(w.id),
            title: w.type === 'watchlist' ? 'Watchlist' : w.type === 'scanner' ? 'Screener' : w.type === 'scanner-short' ? 'Screener Short' : w.type === 'news' ? 'News' : 'Daily Movers',
            // News has NO header count: its rows live inside the widget (it owns
            // its wire subscription — HeatStrip rule), and lifting a count here
            // would re-render this whole tree on every headline burst.
            count: w.type === 'watchlist' ? watch.length : w.type === 'scanner' ? signals.length : w.type === 'scanner-short' ? shortSignals.length : w.type === 'news' ? null : moverRows.length,
            reorder,
          },
            w.type === 'watchlist'
              ? React.createElement(Watchlist, { rows: watchRows, activeSymbol, positions, alarms: ladderAlarms, onSelect: selectSymbol, onRemove: removeFromWatch, onAdd: addToWatch, search: searchSymbols, rowReorder, universe: symbols })
              : w.type === 'scanner'
                ? React.createElement(ScannerList, { signals, paused: sigPaused, soundOn, activeSymbol, lastIds: sigNewIds, onTogglePause: () => setSigPaused((v) => !v), onToggleSound: toggleSound, onClear: () => setSignals([]), onLoad: loadSignal })
                : w.type === 'scanner-short'
                  ? React.createElement(ScannerList, { short: true, signals: shortSignals, paused: shortPaused, soundOn, activeSymbol, lastIds: shortNewIds, onTogglePause: () => setShortPaused((v) => !v), onToggleSound: toggleSound, onClear: () => setShortSignals([]), onLoad: loadSignal })
                  : w.type === 'news'
                    ? React.createElement(NewsRail, { activeSymbol, onSelect: loadNews })
                    : React.createElement(DailyMovers, { rows: moverRows, activeSymbol, positions, onSelect: selectSymbol, onAddToWatchlist: addToWatch })))))),

      React.createElement('div', { className: 'main' },
        // Empty terminal: no active symbols (fresh morning, TTL-pruned watchlist,
        // or the symbols API failing). The rail still renders, so the watchlist
        // search can add the first ticker; a screener signal click works too.
        !hasSymbol ? React.createElement('div', { className: 'main-empty' },
          React.createElement('div', { className: 'me-ic' }, Icon.bolt()),
          React.createElement('div', { className: 'me-t' }, 'No symbols yet'),
          React.createElement('div', { className: 'me-s' },
            'Use “Add or search symbol” in the Watchlist panel to load a ticker, or tap a screener signal when one fires.'),
          React.createElement('button', { className: 'btn me-open', onClick: () => setRailOpen(true) }, 'Open workspace')) : null,
        hasSymbol ? PP('ChartHeader', React.createElement(ChartHeader, {
          symbol: activeSymbol, name: m.name, lastPrice, prevClose: m.prevClose ?? m.base,
          tf, onTf: setTf, indicators, onToggleInd: toggleInd, indOpen, setIndOpen,
          chartType, onChartType: setChartType,
          // Detach = this symbol's full Terminal in its OWN OS window (own
          // WebSocket, identical rendering) — lib/solo.ts. Present in every
          // window: a detached window can detach whatever it charts next.
          onDetach: () => openSoloWindow(activeSymbol, tf),
          // journal toggle + widget reopen pills live IN the header (ported from the
          // prototype) — the old floating card-reopens strip is gone.
          onJournal: journalBook ? () => setTjOpen((v: boolean) => !v) : null, journalOpen: tjOpen,
          tradeSide: sideBySym[activeSymbol] || 'long', onTradeSide: setTradeSide,
          sideLocked: !!(position && position.qty > 0),
          // Borrow held for THIS symbol, next to the side toggle — the flip to
          // short is the moment the number matters.
          heldLocate: heldLocates[activeSymbol] || null,
          // Levels popover: display mode + which level kinds draw (lib/structure.ts).
          levelMode, onLevelMode: setLevelMode, levelPrefs, onLevelPrefs: setLevelPrefs,
          reopens: [
            !docked && !cardOpen ? { key: 'position', label: 'Position', title: 'Open Position card', onClick: () => setCardOpen(true), d: 'M3 3v18h18M7 15l4-5 3 3 5-7' } : null,
            // Ladder. Embedded mode has no chart-top ribbon any more, so this pill
            // IS the ladder affordance — a toggle for the full widget, carrying its
            // open state (hence `on`). Floating mode keeps plain reopen semantics.
            docked
              ? { key: 'ladder', label: 'Ladder', on: ladPop, title: ladPop ? 'Hide the full ladder widget' : 'Open the full ladder widget', onClick: () => setLadPop((v: boolean) => !v), d: 'M8 3v18M16 3v18M8 7h8M8 12h8M8 17h8' }
              : (!ladOpen ? { key: 'ladder', label: 'Ladder', title: 'Open Ladder card', onClick: () => setLadOpen(true), d: 'M8 3v18M16 3v18M8 7h8M8 12h8M8 17h8' } : null),
            (!dilOpen && supplyModel) ? { key: 'supply', label: 'Supply', title: 'Open Supply card', onClick: () => setDilOpen(true), d: 'M3 7l9-4 9 4-9 4-9-4zM3 12l9 4 9-4M3 17l9 4 9-4' } : null,
            !newsOpen ? { key: 'news', label: 'News', title: 'Company overview & latest headlines for ' + activeSymbol, onClick: () => setNewsOpen(true), d: 'M4 5h16v14H4zM8 9h8M8 12h8M8 15h5' } : null,
            (!borrowOpen && borrowData) ? { key: 'borrow', label: 'Borrow', title: 'Borrow & short availability for ' + activeSymbol + ' — locate cost, break-even and size', onClick: () => setBorrowOpen(true), d: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M15 9l-6 6M9.5 9.5h.01M14.5 14.5h.01' } : null,
            (!pblOpen && pblReport) ? { key: 'pbl', label: 'Pullback', title: 'Open Pullback card', onClick: () => setPblOpen(true), d: 'M3 8l5 6 4-4 3 3 6-9' } : null,
            (!rrOpen && rrAvailable) ? { key: 'rr', label: 'R/R', title: 'Live risk / reward for ' + activeSymbol + ' — check the ratio before you take the trade', onClick: () => setRrOpen(true), d: 'M12 4v16M8 20h8M5 8h14M5 8l-2.5 5h5zM19 8l-2.5 5h5z' } : null,
            // Vision detection layer — a chart overlay, not a card, so this pill
            // is a pure toggle carrying its open state (the docked-ladder pattern).
            { key: 'vision', label: 'Vision', on: visionOpen, title: visionOpen ? 'Hide the Vision detection layer' : 'Show the Vision detection layer — structures forming on ' + activeSymbol + ' across 1m/3m/5m/15m', onClick: () => setVisionOpen((v: boolean) => !v), d: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z' },
            // Pullback trend lines — same pattern: a pure toggle over the chart.
            { key: 'trend', label: 'Trend Lines', on: trendOpen, title: trendOpen ? 'Hide the pullback trend lines' : 'Trend Lines — drawing only, 1-minute chart. A ruler from the high of the day across the bounce tops (resistance) and a ruler from the low of the day under the landings (support), both extended right. A break is marked with an × and the ruler kept; a new one is laid from the same anchor across the next bounce. No signals, no orders.', onClick: () => setTrendOpen((v: boolean) => !v), d: 'M3 6l18 7M3 20l18-11' },
          ].filter(Boolean),
        })) : null,
        hasSymbol ? React.createElement('div', { className: 'main-body' + (docked ? ' embed' : '') },
          // The embedded LADDER ribbon that used to sit here as a chart-top band is
          // gone: it cost 66px of chart height permanently to show what the full
          // widget shows better on demand. Its expand button now lives in the header
          // pill row beside Supply / Pullback / journal, and opens the same pop-out.
          React.createElement('div', { className: 'stage', ref: stageRef },
            PP('ChartPane', React.createElement(ChartPane, {
              symbol: activeSymbol, tf, indicators, chartApiRef, colors,
              onPrice, onChartClick, onHover: setHover, live: true,
              padRight: emOn ? emRail + EM_W : 0,
            })),
            // Structure Map (screener levels heatmap). Only renders when zones are present.
            (srView && srView.zones.length)
              ? React.createElement(StructureMap, { chartApiRef, zones: srView.zones, nearSup: srView.nearSup, nearRes: srView.nearRes, atr: srView.atr, lastPrice, onStageOrder: stageFromLevel, lanes2: visionLane === 'mv5' })
              : null,
            React.createElement(CursorLegend, { symbol: activeSymbol, indicators, hover, lastPrice }),
            PP('OrderOverlay', React.createElement(OrderOverlay, {
          side: sideBySym[activeSymbol] || 'long',
              chartApiRef, position, lastPrice, colors, hover, popover, sizeParams,
              onOrderDrag, onOrderCommit, onPlaceOrder: placeOrder, onDismissPopover: () => setPopover(null),
              onReposition: repositionPopover, onCancelOrder: cancelOrder,
            })),
            // Vision detection layer: draws UNDER the order overlay (z-index 5 in
            // vision.css) so order pills stay interactive on top; pointer-events
            // none — the chart stays fully clickable through it.
            visionOpen ? React.createElement(VisionLayer, { chartApiRef, symbol: activeSymbol, tf }) : null,
            // Pullback trend lines: same z-index/pointer contract as the vision
            // layer (trendlines.css); geometry comes from the pane's own candles.
            trendOpen ? React.createElement(TrendLineLayer, { chartApiRef, symbol: activeSymbol, tf }) : null,
            // Entry marks: the 5m / 15m range column against the axis, MV-5's turning point + the chase hatch, the
            // last-price tag's colour (entrymarks.css: z-index 5, under the order pills; the S/R rail and the pills
            // step left by its column while it is mounted).
            emMarks ? React.createElement(EntryMarksLayer, { chartApiRef, symbol: activeSymbol, marks: emMarks, price: lastPrice > 0 ? lastPrice : null, colors, railW: emRail, record: emRecord }) : null,
            // Chart-bottom verdict ribbon: model hold-call + forming read + live
            // R/R values in one band above the time axis. Owns its own feeds
            // (#586 rule); renders null until either feed has a frame.
            React.createElement(VerdictRibbon, { symbol: activeSymbol, lastPrice }),
            toast ? React.createElement('div', { className: String(toast).startsWith('⚠') ? 'toast warn' : 'toast' }, toast) : null),
          // ---- embedded POSITION bar: chart-bottom band, below volume ----
          docked ? PP('PositionBar', React.createElement(PositionBar, {
            side: sideBySym[activeSymbol] || 'long',
            position, symbol: activeSymbol, lastPrice, sizeParams,
            onBuyNow: buyNow, onCloseNow: () => closeNow(), onSellPercent: sellPercent,
            onExpand: () => setPosPop((v: boolean) => !v), expanded: posPop,
          })) : null) : null,

        // ---- EMBEDDED mode pop-outs: full widget on demand ----
        hasSymbol && docked && posPop ? PP('FloatingCard', React.createElement(FloatingCard, {
          side: sideBySym[activeSymbol] || 'long',
          position, symbol: activeSymbol, lastPrice, levels, sizeParams,
          pos: { x: cardPos.x == null ? 400 : cardPos.x, y: cardPos.y }, collapsed: cardCollapsed, colors,
          onMove: setCardPos, onToggle: () => setCardCollapsed((v) => !v), onClose: () => setPosPop(false),
          onBuyNow: buyNow, onCloseNow: () => closeNow(), onSellPercent: sellPercent,
          preset: (position && position.preset) || cfgPreset,
          trailing: position && position.trailing != null ? !!position.trailing : cfgTrailing,
          trailSource: trailSrcBySym[activeSymbol] || 'levels', indSnap,
          onApplyPreset: applyPreset, onToggleTrailing: () => toggleTrailing(),
          onApplyTrailSource: applyTrailSource,
        })) : null,
        docked && ladPop && activeSymbol ? PP('LadderCard', React.createElement(LadderCard, {
          // Owns the audio cue while it is up (the ribbon that used to own it is
          // gone); closed, the watchlist-wide ladder alarm below covers this symbol.
          symbol: activeSymbol, sound: soundOn,
          hist: t.ladderHist, showTape: t.ladderTape !== false, vwap: levels && levels.vwap,
          pos: ladCardPos, collapsed: ladCardCollapsed,
          onMove: setLadCardPos, onToggle: () => setLadCardCollapsed((v: boolean) => !v), onClose: () => setLadPop(false),
        })) : null,

        // ---- floating mode: Position + Ladder as free cards over the chart ----
        hasSymbol && !docked && cardOpen ? PP('FloatingCard', React.createElement(FloatingCard, {
          side: sideBySym[activeSymbol] || 'long',
          position, symbol: activeSymbol, lastPrice, levels, sizeParams,
          pos: { x: cardPos.x == null ? 400 : cardPos.x, y: cardPos.y }, collapsed: cardCollapsed, colors,
          onMove: setCardPos, onToggle: () => setCardCollapsed((v) => !v), onClose: () => setCardOpen(false),
          onBuyNow: buyNow, onCloseNow: () => closeNow(), onSellPercent: sellPercent,
          preset: (position && position.preset) || cfgPreset,
          trailing: position && position.trailing != null ? !!position.trailing : cfgTrailing,
          trailSource: trailSrcBySym[activeSymbol] || 'levels', indSnap,
          onApplyPreset: applyPreset, onToggleTrailing: () => toggleTrailing(),
          onApplyTrailSource: applyTrailSource,
        })) : null,
        !docked && ladOpen && activeSymbol ? PP('LadderCard', React.createElement(LadderCard, {
          symbol: activeSymbol, sound: soundOn,
          hist: t.ladderHist, showTape: t.ladderTape !== false, vwap: levels && levels.vwap,
          pos: ladCardPos, collapsed: ladCardCollapsed,
          onMove: setLadCardPos, onToggle: () => setLadCardCollapsed((v: boolean) => !v), onClose: () => setLadOpen(false),
        })) : null,
        // ---- floating SUPPLY card (both modes, on demand) + its Float & Dilution timeline ----
        dilOpen && supplyModel ? React.createElement(SupplyCard, {
          model: supplyModel, symbol: activeSymbol, lastPrice,
          pos: dilCardPos, collapsed: dilCardCollapsed,
          onMove: setDilCardPos, onToggle: () => setDilCardCollapsed((v: boolean) => !v), onClose: () => setDilOpen(false),
          onOpenTimeline: () => setSupplyTlOpen(true),
        }) : null,
        supplyTlOpen && supplyModel ? React.createElement(SupplyTimeline, {
          model: supplyModel, symbol: activeSymbol, onClose: () => setSupplyTlOpen(false),
        }) : null,
        // ---- floating STOCK INFO / news card (about + headlines, on demand) ----
        newsOpen && activeSymbol ? React.createElement(StockInfoCard, {
          symbol: activeSymbol, name: m.name, lastPrice, prevClose: m.prevClose ?? m.base,
          meta: m, about: aboutInfo, news: newsData, floatShares: supplyModel ? (supplyModel.float.screener ?? supplyModel.float.value) : null,
          pos: newsCardPos, onMove: setNewsCardPos, onClose: () => setNewsOpen(false),
        }) : null,
        // ---- floating BORROW / short-availability card (read-only, on demand) ----
        borrowOpen && borrowData ? React.createElement(BorrowCard, {
          // The card's own hydrate carries `locate` too; the polled map is the
          // fresher of the two, so it wins when present.
          data: heldLocates[activeSymbol] ? { ...borrowData, locate: heldLocates[activeSymbol] } : borrowData,
          symbol: activeSymbol, lastPrice,
          pos: borrowCardPos, collapsed: borrowCardCollapsed,
          onMove: setBorrowCardPos, onToggle: () => setBorrowCardCollapsed((v: boolean) => !v), onClose: () => setBorrowOpen(false),
        }) : null,
        // ---- floating PULLBACK (PBL-v12) signal card (both modes, on demand) ----
        pblOpen && pblReport ? React.createElement(PblCard, {
          report: pblReport, symbol: activeSymbol, lastPrice,
          pos: pblCardPos, collapsed: pblCardCollapsed,
          onMove: setPblCardPos, onToggle: () => setPblCardCollapsed((v: boolean) => !v), onClose: () => setPblOpen(false),
        }) : null,
        // ---- floating TRADE JOURNAL card (both modes, on demand) ----
        tjOpen && journalBook ? React.createElement(JournalCard, {
          book: journalBook, symbol: activeSymbol, lastPrice,
          pos: tjCardPos, collapsed: tjCardCollapsed,
          onMove: setTjCardPos, onToggle: () => setTjCardCollapsed((v: boolean) => !v), onClose: () => setTjOpen(false),
        }) : null,
        // ---- floating LIVE RISK/REWARD card (both modes, on demand) ----
        // The card hydrates + subscribes internally (keyed on symbol, with its
        // own stale-symbol gate) so rr frames never re-render this tree.
        rrOpen && rrAvailable ? React.createElement(RrCard, {
          symbol: activeSymbol, lastPrice,
          pos: rrCardPos, onMove: setRrCardPos, onClose: () => setRrOpen(false),
        }) : null,
        null)));
}
